import { MeinersFootprintControls } from './MeinersFootprintControls';
import { pendingFemaLabel, pendingFemaLabels, PENDING_FEMA_COLOR } from '../../lib/fema-pending';
import { pipelineCapacityDisplay } from '../../lib/pipeline-capacity';
import { meinersPipelineCapacity } from '../../lib/meiners-pipeline-capacity';
import React, { useRef, useEffect, useState, useImperativeHandle, forwardRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useMapMeasurement } from '../../hooks/useMapMeasurement';
import { buildSitePadFilter } from './sitePadFilter';
import mapboxgl from 'mapbox-gl';
import circle from '@turf/circle';
import { RotateCcw, Ruler, SquareDashed } from 'lucide-react';
import { getMapboxToken } from '../../lib/config';
import { useScrollSync, type BriefingSection } from './ScrollSyncProvider';
import type { ParcelDetail, NearbyPipeline, BriefingMapLayers, NearestFeature, OperatorCarveOut, MeinersFootprintData } from '../../lib/api';
import { roadSurfaceShort, roadSurfaceFromCode, truckShareWords, feetInches, trafficWord } from '../../lib/api';
import { isDesertSW } from '../../lib/parcel-analysis';
import { BriefingLayerControls, type BriefingLayerControlsHandle } from './BriefingLayerControls';
import { LAYER_DEFINITIONS, type BriefingLayerData, type MapEmphasis } from './briefing-layer-data';
import { acRange, meanRange } from './slopeRange';

export interface BriefingMapHandle {
  captureSnapshots: () => Promise<{ cover: string; fuelDelivery: string; infrastructure: string }>;
}

interface BriefingMapProps {
  parcel: ParcelDetail;
  pipelines: NearbyPipeline[];
  mapLayers: BriefingMapLayers;
  nearestFeatures?: Record<string, NearestFeature | null | undefined>;
  carveOuts?: OperatorCarveOut[];
  meinersFootprint?: MeinersFootprintData;
  // SITE LAYOUT switcher (A1 Alt only). Controlled when the parent supplies
  // both — SiteBriefing lifts the layout so the text can follow the map —
  // otherwise the map keeps the selection itself.
  siteLayout?: string;
  onSiteLayoutChange?: (key: string) => void;
  onPrint?: () => void;
  onShare?: () => void;
  parcelId?: string;
  token?: string;
  className?: string;
  style?: React.CSSProperties;
}

const EMPTY_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

interface MapCameraState {
  center: [number, number];
  zoom: number;
  bearing: number;
  pitch: number;
  padding: mapboxgl.PaddingOptions;
}

type PaintSnapshot = {
  visibility: string;
  properties: Record<string, unknown>;
};

function clonePaintValue(value: unknown): unknown {
  return value === undefined ? undefined : JSON.parse(JSON.stringify(value));
}

// Gas transmission styling: width by pipe diameter (2.5 px under 16 in, 3.5 px to
// 30 in, 5 px at 30 in and over), a casing 2.5 px wider, a name · diameter label,
// and the headline filter (the line the briefing's MW figure is about). Diameter
// comes from the feature itself when the API serves it, else by name from the Fuel
// Delivery list — lines beyond the search radius fall to the default width.
type GasStyle = { width: unknown; casingWidth: unknown; labelText: unknown; headlineFilter: unknown };
const GAS_NAME_EXPR = ['upcase', ['coalesce', ['get', 'name'], '']];
const gasWidthForDiameter = (d: number) => (d >= 30 ? 5 : d >= 16 ? 3.5 : 2.5);
function buildGasStyle(pipelines: NearbyPipeline[] | null | undefined, headlineName: string | null | undefined): GasStyle {
  const diaByName = new Map<string, number>();
  const headline = new Set<string>();
  for (const pl of pipelines || []) {
    const rows = [{ name: pl.name, dia: pl.diameter_inches }, ...(pl.segments || []).map(s => ({ name: s.name, dia: s.diameter_inches }))];
    for (const r of rows) {
      if (!r.name || r.dia == null) continue;
      const k = r.name.toUpperCase();
      diaByName.set(k, Math.max(diaByName.get(k) ?? 0, Number(r.dia)));
    }
    if ((pl.headline_override || pl.co_headline) && pl.name) headline.add(pl.name.toUpperCase());
  }
  if (headline.size === 0 && headlineName) headline.add(headlineName.toUpperCase());
  const nameOnly = ['coalesce', ['get', 'name'], ''];
  const byNameWidth = diaByName.size
    ? ['match', GAS_NAME_EXPR, ...[...diaByName].flatMap(([n, d]) => [n, gasWidthForDiameter(d)]), 2.5]
    : 2.5;
  const byNameLabel = diaByName.size
    ? ['match', GAS_NAME_EXPR, ...[...diaByName].flatMap(([n, d]) => [n, `${n} · ${d}″`]), nameOnly]
    : nameOnly;
  const featureDia = ['to-number', ['get', 'diameter_inches'], 0];
  const width = ['case', ['>', featureDia, 0], ['case', ['>=', featureDia, 30], 5, ['>=', featureDia, 16], 3.5, 2.5], byNameWidth];
  return {
    width,
    casingWidth: ['+', width, 2.5],
    labelText: ['case', ['>', featureDia, 0], ['concat', nameOnly, ' · ', ['to-string', ['get', 'diameter_inches']], '″'], byNameLabel],
    headlineFilter: ['in', GAS_NAME_EXPR, ['literal', [...headline]]],
  };
}

const ALL_LAYERS = [
  'briefing-parcel-fill',
  'briefing-parcel-outline',
  'briefing-carve-out-fill',
  'briefing-carve-out-outline',
  'briefing-pipelines-gas-glow',
  'briefing-pipelines-gas-casing',
  'briefing-pipelines-gas',
  'briefing-pipelines-gas-label',
  'briefing-pipelines-gathering',
  'briefing-pipelines-desert-sw',
  'briefing-pipelines-desert-sw-glow',
  'briefing-substations',
  'briefing-fiber-fill',
  'briefing-fiber-outline',
  'briefing-carrier-fiber',
  'briefing-osm-switchyards',
  'briefing-water-wells',
  'briefing-data-centers',
  'briefing-wetlands-fill',
  'briefing-wetlands-outline',
  'briefing-airports',
  'briefing-aquifer-fill',
  'briefing-aquifer-outline',
  'briefing-flood-zones-fill',
  'briefing-flood-zones-outline',
  'briefing-flood-zones-twdb-outline',
  'briefing-flood-zones-pending-outline',
  'briefing-highways',
  'briefing-gas-plants',
  'briefing-compressors',
  'briefing-railroads',
  'briefing-oil-gas-laterals',
  'briefing-oil-gas-wells',
  'briefing-public-roads',
  'briefing-access-route-casing',
  'briefing-access-route',
  'briefing-access-points',
  'briefing-frontage-roads',
  'briefing-frontage-tracks',
  'briefing-annotations-line',
  'briefing-annotations-label',
] as const;

// Map badge layer IDs to mapLayers keys — used to hide layers with 0 features
const LAYER_DATA_KEY: Record<string, keyof BriefingMapLayers> = {
  'briefing-pipelines-desert-sw': 'pipelinesDesertSw',
  'briefing-pipelines-gas': 'pipelinesGas',
  'briefing-pipelines-gathering': 'pipelinesGathering',
  'briefing-substations': 'substations',
  'briefing-fiber-fill': 'fiberCoverage',
  'briefing-carrier-fiber': 'carrierRoutes',
  'briefing-osm-switchyards': 'osmSwitchyards',
  'briefing-water-wells': 'waterWells',
  'briefing-data-centers': 'dataCenters',
  'briefing-wetlands-fill': 'wetlands',
  'briefing-aquifer-fill': 'aquifers',
  'briefing-flood-zones-fill': 'floodZones',
  'briefing-highways': 'highways',
  'briefing-gas-plants': 'gasPlants',
  'briefing-compressors': 'compressors',
  'briefing-railroads': 'railroads',
  'briefing-airports': 'airports',
  'briefing-oil-gas-wells': 'oilGasWells',
  'briefing-access-route': 'accessRoute',
  'briefing-frontage-roads': 'frontageRoads',
  'briefing-public-roads': 'publicRoads',
};

const RADIUS_RING_LAYERS = [
  'briefing-radius-2mi-fill', 'briefing-radius-2mi-line',
  'briefing-radius-5mi-fill', 'briefing-radius-5mi-line',
  'briefing-radius-10mi-fill', 'briefing-radius-10mi-line',
  'briefing-radius-labels',
] as const;

const CLICKABLE_PIPELINE_LAYERS = [
  'briefing-pipelines-desert-sw',
  'briefing-pipelines-gas',
  'briefing-pipelines-gathering',
] as const;

const CLICKABLE_FEATURE_LAYERS = [
  'briefing-substations',
  'briefing-osm-switchyards',
  'briefing-gas-plants',
  'briefing-compressors',
  'briefing-data-centers',
  'briefing-carrier-fiber',
  'briefing-fiber-fill',
  'briefing-railroads',
  'briefing-water-wells',
  'briefing-wetlands-fill',
  'briefing-airports',
  'briefing-highways',
  'briefing-flood-zones-fill',
  'briefing-oil-gas-wells',
  'briefing-public-roads',
  'briefing-access-route',
  'briefing-access-points',
  'briefing-frontage-roads',
  'briefing-frontage-tracks',
] as const;

// Sources owned by the source/layer-setup effect. Listed here so the effect can
// idempotently remove them at the start of each run, fixing the StrictMode
// double-mount crash and supporting dep-change re-fires (carveOuts async load,
// mapLayers toggles, radiusRingsFC updates).
const BRIEFING_EFFECT_SOURCES = [
  'briefing-parcel',
  'briefing-carve-out',
  'briefing-annotations',
  'briefing-pipelines-gas',
  'briefing-pipelines-desert-sw',
  'briefing-pipelines-gathering',
  'briefing-substations',
  'briefing-fiber',
  'briefing-aquifer',
  'briefing-flood-zones',
  'briefing-highways',
  'briefing-gas-plants',
  'briefing-compressors',
  'briefing-railroads',
  'briefing-carrier-fiber',
  'briefing-osm-switchyards',
  'briefing-water-wells',
  'briefing-data-centers',
  'briefing-wetlands',
  'briefing-airports',
  'briefing-radius-rings',
  'briefing-public-roads',
  'briefing-site-band',
  'briefing-site-alt',
  'briefing-site-alt-pieces',
  'briefing-site-pads',
  'briefing-site-original-ghost',
] as const;

// Site-layout layer groups (A1 only). Kept out of ALL_LAYERS so the
// section-scroll and full-view visibility loops never touch them — the SITE
// LAYOUT switcher owns their visibility.
const ORIGINAL_CORE_LAYERS = [
  'briefing-parcel-fill', 'briefing-parcel-outline',
  'briefing-carve-out-fill', 'briefing-carve-out-outline',
] as const;
const SITE_BAND_LAYERS = [
  'briefing-site-band-fill', 'briefing-site-band-casing', 'briefing-site-band-outline', 'briefing-site-band-label',
] as const;
const SITE_ALT_LAYERS = [
  'briefing-site-alt-fill', 'briefing-site-alt-casing', 'briefing-site-alt-outline', 'briefing-site-alt-label',
] as const;
const SITE_GHOST_LAYERS = [
  'briefing-site-original-ghost-casing', 'briefing-site-original-ghost-outline',
] as const;
// Ghost tracts: the added / dropped pieces of the selected alternative.
const SITE_PIECE_LAYERS = [
  'briefing-site-alt-pieces-fill', 'briefing-site-alt-pieces-outline', 'briefing-site-alt-pieces-label',
] as const;
// Best clean pad rectangles per layout side (A1 Alt only).
const SITE_PAD_LAYERS = [
  'briefing-site-pads-fill', 'briefing-site-pads-line', 'briefing-site-pads-label',
] as const;

// Enterprise carrier routes provide the fiber map context. FCC coverage remains
// in the source payload but does not establish enterprise connectivity.
const FULL_VIEW_LAYERS = [
  'briefing-pipelines-desert-sw',
  'briefing-pipelines-desert-sw-glow',
  'briefing-pipelines-gas-glow',
  'briefing-pipelines-gas-casing',
  'briefing-pipelines-gas',
  'briefing-pipelines-gas-label',
  'briefing-pipelines-gathering',
  'briefing-substations',
  'briefing-carrier-fiber',
  'briefing-osm-switchyards',
  'briefing-water-wells',
  'briefing-data-centers',
  'briefing-wetlands-fill',
  'briefing-wetlands-outline',
  'briefing-airports',
  'briefing-aquifer-fill',
  'briefing-aquifer-outline',
  'briefing-flood-zones-fill',
  'briefing-flood-zones-outline',
  'briefing-flood-zones-twdb-outline',
  'briefing-flood-zones-pending-outline',
  'briefing-highways',
  'briefing-gas-plants',
  'briefing-compressors',
  'briefing-railroads',
  'briefing-public-roads',
  'briefing-access-route-casing',
  'briefing-access-route',
  'briefing-access-points',
  'briefing-frontage-roads',
  'briefing-frontage-tracks',
  'briefing-oil-gas-laterals',
  'briefing-oil-gas-wells',
] as const;

const FULL_VIEW_OPACITY_OVERRIDES: Record<string, Record<string, number>> = {
  'briefing-pipelines-gathering': { 'line-opacity': 0.4 },
  'briefing-oil-gas-laterals': { 'line-opacity': 0.45 },
  'briefing-aquifer-fill': { 'fill-opacity': 0.15 },
  'briefing-wetlands-fill': { 'fill-opacity': 0.2 },
};

// Map badge layer IDs to the actual map layer group they represent
const LAYER_GROUP: Record<string, string[]> = {
  'briefing-pipelines-desert-sw': ['briefing-pipelines-desert-sw', 'briefing-pipelines-desert-sw-glow'],
  'briefing-pipelines-gas': ['briefing-pipelines-gas-glow', 'briefing-pipelines-gas-casing', 'briefing-pipelines-gas', 'briefing-pipelines-gas-label'],
  'briefing-pipelines-gathering': ['briefing-pipelines-gathering'],
  'briefing-substations': ['briefing-substations'],
  'briefing-fiber-fill': ['briefing-fiber-fill', 'briefing-fiber-outline'],
  'briefing-carrier-fiber': ['briefing-carrier-fiber'],
  'briefing-osm-switchyards': ['briefing-osm-switchyards'],
  'briefing-water-wells': ['briefing-water-wells'],
  'briefing-data-centers': ['briefing-data-centers'],
  'briefing-wetlands-fill': ['briefing-wetlands-fill', 'briefing-wetlands-outline'],
  'briefing-airports': ['briefing-airports'],
  'briefing-aquifer-fill': ['briefing-aquifer-fill', 'briefing-aquifer-outline'],
  'briefing-flood-zones-fill': ['briefing-flood-zones-fill', 'briefing-flood-zones-outline', 'briefing-flood-zones-twdb-outline', 'briefing-flood-zones-pending-outline'],
  'briefing-highways': ['briefing-highways'],
  'briefing-gas-plants': ['briefing-gas-plants'],
  'briefing-compressors': ['briefing-compressors'],
  'briefing-railroads': ['briefing-railroads'],
  'briefing-oil-gas-wells': ['briefing-oil-gas-wells', 'briefing-oil-gas-laterals'],
  'briefing-access-route': ['briefing-access-route-casing', 'briefing-access-route', 'briefing-access-points'],
  'briefing-frontage-roads': ['briefing-frontage-roads', 'briefing-frontage-tracks'],
  'briefing-public-roads': ['briefing-public-roads'],
};

// Default paint values per layer — single source of truth for resets
const DEFAULT_PAINT: Record<string, Record<string, number | string>> = {
  'briefing-pipelines-desert-sw': { 'line-width': 5, 'line-opacity': 1 },
  'briefing-pipelines-desert-sw-glow': { 'line-width': 14, 'line-opacity': 0.3 },
  // Gas transmission and casing widths are diameter expressions (see buildGasStyle);
  // highlightLayer(null) re-applies them, so only opacity lives in this table.
  'briefing-pipelines-gas': { 'line-opacity': 1 },
  'briefing-pipelines-gas-casing': { 'line-opacity': 0.65 },
  'briefing-pipelines-gas-glow': { 'line-width': 14, 'line-opacity': 0.3 },
  'briefing-pipelines-gathering': { 'line-width': 1.5, 'line-opacity': 0.7 },
  'briefing-substations': { 'circle-radius': 5, 'circle-stroke-width': 1.5, 'circle-opacity': 1 },
  'briefing-fiber-fill': { 'fill-opacity': 0.35 },
  'briefing-fiber-outline': { 'line-width': 1.5, 'line-opacity': 1, 'line-color': '#506272' },
  'briefing-carrier-fiber': { 'line-width': 2.5, 'line-opacity': 1 },
  'briefing-osm-switchyards': { 'circle-radius': 6, 'circle-stroke-width': 1.5, 'circle-opacity': 1 },
  'briefing-water-wells': { 'circle-radius': 4, 'circle-stroke-width': 1, 'circle-opacity': 0.9 },
  'briefing-data-centers': { 'circle-radius': 7, 'circle-stroke-width': 2, 'circle-opacity': 1 },
  'briefing-airports': { 'circle-radius': 6, 'circle-stroke-width': 1.5, 'circle-opacity': 0.9 },
  'briefing-wetlands-fill': { 'fill-opacity': 0.25 },
  'briefing-wetlands-outline': { 'line-width': 1, 'line-opacity': 0.5 },
  'briefing-aquifer-fill': { 'fill-opacity': 0.15 },
  'briefing-aquifer-outline': { 'line-width': 1.5, 'line-opacity': 0.5 },
  'briefing-flood-zones-fill': { 'fill-opacity': 0.25 },
  'briefing-flood-zones-outline': { 'line-width': 1, 'line-opacity': 0.6 },
  'briefing-flood-zones-twdb-outline': { 'line-width': 1.2, 'line-opacity': 0.8 },
  'briefing-flood-zones-pending-outline': { 'line-width': 1.2, 'line-opacity': 0.85 },
  'briefing-highways': { 'line-width': 2, 'line-opacity': 0.7 },
  'briefing-gas-plants': { 'circle-radius': 6, 'circle-stroke-width': 1.5, 'circle-opacity': 0.9 },
  'briefing-compressors': { 'circle-radius': 7, 'circle-stroke-width': 2, 'circle-opacity': 1 },
  'briefing-railroads': { 'line-width': 3, 'line-opacity': 1, 'line-color': '#C8CCD0' },
  'briefing-oil-gas-wells': { 'circle-radius': 4.5, 'circle-stroke-width': 1.5, 'circle-opacity': 1 },
  'briefing-oil-gas-laterals': { 'line-width': 1.2, 'line-opacity': 0.7 },
  'briefing-access-route-casing': { 'line-width': 6, 'line-opacity': 0.55 },
  'briefing-access-route': { 'line-width': 3, 'line-opacity': 1 },
  'briefing-access-points': { 'circle-stroke-width': 1.5, 'circle-opacity': 1 },
  'briefing-frontage-roads': { 'line-width': 3.5, 'line-opacity': 0.95 },
  'briefing-frontage-tracks': { 'line-width': 1.5, 'line-opacity': 0.8 },
  // Width is a class expression (see the addLayerGroup call); only opacity resets here.
  'briefing-public-roads': { 'line-opacity': 0.75 },
  // Site Intelligence 'parcel' card hover thickens the premises outline; listed so the
  // reset loop restores it (nothing else repaints these two).
  'briefing-parcel-outline': { 'line-width': 3, 'line-opacity': 0.9 },
  'briefing-carve-out-outline': { 'line-width': 2, 'line-opacity': 0.85 },
};

type SectionMapState = {
  layers: readonly string[];
  zoom: number;
  annotations?: 'pipeline' | 'substation' | null;
};

const SECTION_MAP_STATES: Record<BriefingSection, SectionMapState> = {
  'header': { layers: [], zoom: 10.5 },
  'kill-sheet': { layers: [], zoom: 10.5 },
  'gas-infrastructure': { layers: ['briefing-pipelines-desert-sw', 'briefing-pipelines-desert-sw-glow', 'briefing-pipelines-gas-glow', 'briefing-pipelines-gas-casing', 'briefing-pipelines-gas', 'briefing-pipelines-gas-label', 'briefing-pipelines-gathering', 'briefing-compressors'], zoom: 12, annotations: 'pipeline' },
  'permitting': { layers: ['briefing-wetlands-fill', 'briefing-wetlands-outline', 'briefing-flood-zones-fill', 'briefing-flood-zones-outline'], zoom: 12 },
  'physical-site': { layers: ['briefing-wetlands-fill', 'briefing-wetlands-outline', 'briefing-flood-zones-fill', 'briefing-flood-zones-outline', 'briefing-oil-gas-laterals', 'briefing-oil-gas-wells'], zoom: 14 },
  'land-ownership': { layers: [], zoom: 14 },
  'infrastructure': { layers: ['briefing-substations', 'briefing-osm-switchyards', 'briefing-airports', 'briefing-carrier-fiber', 'briefing-highways', 'briefing-public-roads', 'briefing-railroads', 'briefing-access-route-casing', 'briefing-access-route', 'briefing-access-points', 'briefing-frontage-roads', 'briefing-frontage-tracks'], zoom: 11, annotations: 'substation' },
  'power-infrastructure': { layers: ['briefing-substations', 'briefing-osm-switchyards', 'briefing-airports', 'briefing-carrier-fiber', 'briefing-highways', 'briefing-public-roads', 'briefing-railroads', 'briefing-access-route-casing', 'briefing-access-route', 'briefing-access-points', 'briefing-frontage-roads', 'briefing-frontage-tracks'], zoom: 11, annotations: 'substation' },
  'water-sourcing': { layers: ['briefing-water-wells'], zoom: 12 },
  'fiber-connectivity': { layers: ['briefing-carrier-fiber'], zoom: 11 },
  'mine-hazards': { layers: [], zoom: 12 },
  'oil-gas-wells': { layers: ['briefing-oil-gas-wells', 'briefing-oil-gas-laterals', 'briefing-pipelines-gathering'], zoom: 13 },
  'risk-register': { layers: ['briefing-pipelines-desert-sw', 'briefing-pipelines-desert-sw-glow', 'briefing-pipelines-gas-glow', 'briefing-pipelines-gas-casing', 'briefing-pipelines-gas', 'briefing-pipelines-gas-label', 'briefing-substations', 'briefing-data-centers'], zoom: 9 },
  'data-confidence': { layers: [], zoom: 14 },
};

const SECTION_LABELS = {
  'header': 'Overview',
  'kill-sheet': 'Site Assessment',
  'gas-infrastructure': 'Fuel Delivery',
  'permitting': 'Permitting',
  'physical-site': 'Physical Site',
  'land-ownership': 'Land & Ownership',
  'infrastructure': 'Site Access',
  'power-infrastructure': 'Power Infrastructure',
  'water-sourcing': 'Water Sourcing',
  'fiber-connectivity': 'Fiber Connectivity',
  'mine-hazards': 'Mine Hazards',
  'oil-gas-wells': 'Oil & Gas Wells',
  'risk-register': 'Risk Register',
  'data-confidence': 'Data Confidence',
} satisfies Record<BriefingSection, string>;

export const BriefingMap = forwardRef<BriefingMapHandle, BriefingMapProps>(({ parcel, pipelines, mapLayers, nearestFeatures, carveOuts, meinersFootprint, siteLayout: siteLayoutProp, onSiteLayoutChange, onPrint, onShare, parcelId, token, className, style }, ref) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const layerControlsRef = useRef<BriefingLayerControlsHandle | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [layerEmphasis, setLayerEmphasis] = useState<MapEmphasis | null>(null);
  const emphasisSnapshotRef = useRef<Map<string, PaintSnapshot>>(new Map());
  const homeCameraRef = useRef<MapCameraState | null>(null);
  const [accessTractVisible, setAccessTractVisible] = useState(true);
  // SITE LAYOUT switcher (A1 only): 'original' = analyzed core + western band,
  // otherwise a seller-proposed alternative's feature `key`.
  // Ghost-layer toggle (A1 Alt only): CHANGES = the added/dropped pieces of the
  // selected alternative, default OFF so the map opens clean.
  // Acreage boxes default OFF (Matthew: the map opens clean; one chip away).
  const [padsVisible, setPadsVisible] = useState(false);
  const padsVisibleRef = useRef(padsVisible); padsVisibleRef.current = padsVisible;
  const [piecesVisible, setPiecesVisible] = useState(false);
  const piecesVisibleRef = useRef(piecesVisible); piecesVisibleRef.current = piecesVisible;
  // Map-native distance and area tools. One is armed at a time; the ref folds
  // both into the guard used by the briefing's click and hover handlers.
  const [measureActive, setMeasureActive] = useState(false);
  const [areaActive, setAreaActive] = useState(false);
  const measureActiveRef = useRef(false);
  measureActiveRef.current = measureActive || areaActive;
  const distanceActiveRef = useRef(false);
  distanceActiveRef.current = measureActive;
  const armMeasure = useCallback((v: boolean) => { setMeasureActive(v); if (v) setAreaActive(false); }, []);
  const armArea = useCallback((v: boolean) => { setAreaActive(v); if (v) setMeasureActive(false); }, []);
  const disarmArea = useCallback(() => setAreaActive(false), []);
  const [internalSiteLayout, setInternalSiteLayout] = useState<string>('original');
  const siteLayout = siteLayoutProp ?? internalSiteLayout;
  const setSiteLayout = useCallback((key: string) => {
    setInternalSiteLayout(key);
    onSiteLayoutChange?.(key);
  }, [onSiteLayoutChange]);
  // Always-current mirror, read by effects that must not re-run on a switch
  // (the source/layer setup and the guided/full-view visibility effects).
  const siteLayoutRef = useRef(siteLayout);
  siteLayoutRef.current = siteLayout;
  // Original-layout geometry (core + band), stashed by the setup effect for the
  // switcher's fitBounds so the repCarveOut resolution isn't duplicated.
  const originalGhostRef = useRef<GeoJSON.Feature[]>([]);
  // Last layout the camera was moved for — null until the switcher's first run,
  // which never moves the camera.
  const cameraLayoutRef = useRef<string | null>(null);
  const measure = useMapMeasurement({
    mapRef, ready: mapLoaded, styleEpoch: 0,
    active: measureActive, areaActive, onExitArea: disarmArea,
    enabled: true,
  });
  const disarmMeasurements = useCallback(() => {
    if (distanceActiveRef.current) measure.finish();
    setMeasureActive(false);
    setAreaActive(false);
  }, [measure.finish]);
  const changeSiteLayout = useCallback((key: string) => {
    disarmMeasurements();
    setSiteLayout(key);
  }, [disarmMeasurements, setSiteLayout]);
  const { activeSection, hoverFeature, isFullView, briefingMode, footprintScreen } = useScrollSync();
  const prevSectionRef = useRef<BriefingSection>(activeSection);
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  // Hover-spawned pipeline card — separate ref so panel-row hover never fights
  // a click-opened popup; removed on every hover change.
  const hoverPopupRef = useRef<mapboxgl.Popup | null>(null);

  // Only show layers that have actual features for this parcel
  const populatedLayers = useMemo(() => {
    const set = new Set<string>();
    for (const [layerId, dataKey] of Object.entries(LAYER_DATA_KEY)) {
      const fc = mapLayers[dataKey];
      if (fc?.features?.length > 0) set.add(layerId);
    }
    return set;
  }, [mapLayers]);

  // Derive active layers for overlay badge — filter out empty layers
  const effectiveSection = briefingMode === 'checklist' ? 'header' : activeSection;

  // Alternative site layouts (A1 only) — chips + the caption under them.
  const siteAlternativeFeatures = mapLayers.siteAlternatives?.features || [];
  const activeAlternative = siteLayout === 'original'
    ? null
    : siteAlternativeFeatures.find(f => f.properties?.key === siteLayout) || null;
  const illustrativeAcreage = activeAlternative?.properties?.pads_status === 'illustrative';

  const baseSiteGeometry = useMemo<GeoJSON.Geometry | null>(() => {
    const representative = (carveOuts || []).find(carveOut =>
      carveOut.geojson && (carveOut.parcelIds || []).includes(parcel.id)
    );
    if (representative?.geojson) return representative.geojson as GeoJSON.Geometry;
    if (!parcel.geojson) return null;
    return typeof parcel.geojson === 'string'
      ? JSON.parse(parcel.geojson) as GeoJSON.Geometry
      : parcel.geojson;
  }, [carveOuts, parcel.geojson, parcel.id]);

  const displayedFootprint = useMemo<GeoJSON.FeatureCollection>(() => {
    const features: GeoJSON.Feature[] = [];
    if (activeAlternative?.geometry.type === 'Polygon' || activeAlternative?.geometry.type === 'MultiPolygon') {
      features.push({ type: 'Feature', properties: activeAlternative.properties || {}, geometry: activeAlternative.geometry });
    } else if (baseSiteGeometry?.type === 'Polygon' || baseSiteGeometry?.type === 'MultiPolygon') {
      features.push({ type: 'Feature', properties: {}, geometry: baseSiteGeometry });
      if (accessTractVisible) {
        for (const feature of mapLayers.siteBand?.features || []) {
          if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') features.push(feature);
        }
      }
    }
    return { type: 'FeatureCollection', features };
  }, [accessTractVisible, activeAlternative, baseSiteGeometry, mapLayers.siteBand]);

  const layerData = useMemo<BriefingLayerData>(() => ({
    parcel, pipelines, mapLayers, nearestFeatures,
    carveOutSlug: carveOuts?.find(co => co.geojson && co.parcelIds?.includes(parcel.id))?.slug,
  }), [mapLayers, nearestFeatures, parcel, pipelines, carveOuts]);
  const availableMemberIds = useMemo(() => {
    const ids = new Set(populatedLayers);
    if (nearestFeatures?.substation || nearestFeatures?.secondSubstation) ids.add('briefing-substations');
    if (nearestFeatures?.airport || nearestFeatures?.majorAirport || nearestFeatures?.nearestCommercial || nearestFeatures?.pavedRunway) {
      ids.add('briefing-airports');
    }
    return LAYER_DEFINITIONS.filter(definition => ids.has(definition.id)).map(definition => definition.id);
  }, [nearestFeatures, populatedLayers]);
  const layerViewKey = `${parcel.id}|${siteLayout}|${briefingMode === 'checklist' ? 'checklist' : isFullView ? 'full' : `guided:${effectiveSection}`}`;

  // Keyed on stable fields so the layer-setup effect (which reads it) is not re-fired
  // by a parcel object identity change; pipelines and the headline arrive with mapLayers.
  const gasStyle = useMemo(() => buildGasStyle(pipelines, parcel.pipeline_available_name), [pipelines, parcel.pipeline_available_name]);
  const applyGasWidths = useCallback((m: mapboxgl.Map) => {
    if (m.getLayer('briefing-pipelines-gas')) m.setPaintProperty('briefing-pipelines-gas', 'line-width', gasStyle.width as any);
    if (m.getLayer('briefing-pipelines-gas-casing')) m.setPaintProperty('briefing-pipelines-gas-casing', 'line-width', gasStyle.casingWidth as any);
  }, [gasStyle]);

  // Layer badge hover: highlight the hovered layer, dim others
  const highlightLayer = useCallback((badgeLayerId: string | null) => {
    const m = mapRef.current;
    if (!m || !mapLoaded) return;

    if (!badgeLayerId) {
      // Reset all layers to defaults from DEFAULT_PAINT table
      for (const group of Object.values(LAYER_GROUP)) {
        for (const id of group) {
          const defaults = DEFAULT_PAINT[id];
          if (!defaults || !m.getLayer(id)) continue;
          for (const [prop, val] of Object.entries(defaults)) {
            m.setPaintProperty(id, prop, val);
          }
        }
      }
      applyGasWidths(m);
      return;
    }

    // Accentuate ONLY the hovered layer — don't touch others
    const group = LAYER_GROUP[badgeLayerId];
    if (!group) return;
    for (const id of group) {
      if (!m.getLayer(id)) continue;
      const layer = m.getLayer(id)!;
      if (layer.type === 'line') {
        m.setPaintProperty(id, 'line-opacity', 1);
        if (id === 'briefing-pipelines-desert-sw') m.setPaintProperty(id, 'line-width', 10);
        else if (id === 'briefing-pipelines-desert-sw-glow') { m.setPaintProperty(id, 'line-width', 24); m.setPaintProperty(id, 'line-opacity', 0.5); }
        else if (id === 'briefing-pipelines-gas') m.setPaintProperty(id, 'line-width', 6);
        else if (id === 'briefing-pipelines-gas-casing') { m.setPaintProperty(id, 'line-width', 8.5); m.setPaintProperty(id, 'line-opacity', 0.8); }
        else if (id === 'briefing-pipelines-gas-glow') { m.setPaintProperty(id, 'line-width', 22); m.setPaintProperty(id, 'line-opacity', 0.5); }
        else if (id === 'briefing-pipelines-gathering') m.setPaintProperty(id, 'line-width', 4);
        else if (id === 'briefing-fiber-outline') { m.setPaintProperty(id, 'line-width', 2); m.setPaintProperty(id, 'line-color', '#E4E6EA'); }
        else if (id === 'briefing-aquifer-outline') m.setPaintProperty(id, 'line-width', 3);
        else if (id === 'briefing-highways') m.setPaintProperty(id, 'line-width', 5);
        else if (id === 'briefing-railroads') m.setPaintProperty(id, 'line-width', 5);
        else if (id === 'briefing-carrier-fiber') m.setPaintProperty(id, 'line-width', 5);
        else if (id === 'briefing-wetlands-outline') m.setPaintProperty(id, 'line-width', 2);
      } else if (layer.type === 'fill') {
        m.setPaintProperty(id, 'fill-opacity', id === 'briefing-fiber-fill' ? 0.55 : id.includes('aquifer') || id.includes('wetland') ? 0.4 : 1);
      } else if (layer.type === 'circle') {
        m.setPaintProperty(id, 'circle-opacity', 1);
        m.setPaintProperty(id, 'circle-radius', 12);
        m.setPaintProperty(id, 'circle-stroke-width', 3);
      }
    }
  }, [mapLoaded, applyGasWidths]);

  // Memoized hover config — only recomputes when parcel/mapLayers change, not on every hover
  const hoverConfig = useMemo(() => ({
    'highway': { fc: mapLayers.highways, miles: parcel.nearest_highway_miles != null ? Number(parcel.nearest_highway_miles) : null, name: parcel.nearest_highway_name || 'Highway' },
    'gas-plant': { fc: mapLayers.gasPlants, miles: parcel.nearest_gas_plant_miles != null ? Number(parcel.nearest_gas_plant_miles) : null, name: 'Gas Plant' },
    'railroad': { fc: mapLayers.railroads, miles: parcel.nearest_railroad_miles != null ? Number(parcel.nearest_railroad_miles) : null, name: parcel.nearest_railroad_owner || 'Railroad' },
    'fiber': { fc: mapLayers.carrierRoutes, miles: parcel.nearest_carrier_fiber_miles != null ? Number(parcel.nearest_carrier_fiber_miles) : null, name: 'Carrier route' },
  }), [parcel, mapLayers]);

  const radiusRingsFC = useMemo((): GeoJSON.FeatureCollection => {
    const center: [number, number] = [Number(parcel.lng), Number(parcel.lat)];
    const radii = [
      { miles: 2, label: '2 mi' },
      { miles: 5, label: '5 mi' },
      { miles: 10, label: '10 mi' },
    ];
    return {
      type: 'FeatureCollection',
      features: radii.flatMap(r => {
        const poly = circle(center, r.miles, { units: 'miles', steps: 64 });
        poly.properties = { radius: r.miles, label: r.label };
        // Label point at east edge of ring
        const coords = poly.geometry.coordinates[0];
        const eastPt = coords.reduce((best, c) => (c[0] > best[0] ? c : best), coords[0]);
        return [
          poly,
          { type: 'Feature' as const, geometry: { type: 'Point' as const, coordinates: eastPt }, properties: { label: r.label } },
        ];
      }),
    };
  }, [parcel.lng, parcel.lat]);

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    mapboxgl.accessToken = getMapboxToken();

    const m = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      center: [Number(parcel.lng), Number(parcel.lat)],
      zoom: 10.5,
      preserveDrawingBuffer: true,
      attributionControl: false,
    });

    m.addControl(new mapboxgl.NavigationControl(), 'top-right');
    m.addControl(new mapboxgl.AttributionControl({ compact: false }), 'bottom-right');

    m.on('load', () => {
      // Blue Ridge atmosphere — hazy morning tint
      m.setFog({
        color: '#E4E6E8',         // haze
        'high-color': '#8A9CAA',  // distant-ridge
        'horizon-blend': 0.08,
        'space-color': '#2E3840', // near-ridge
        'star-intensity': 0,
      });
      mapRef.current = m;
      setMapLoaded(true);
    });

    return () => {
      if (popupRef.current) popupRef.current.remove();
      mapRef.current = null;
      m.remove();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Add sources and layers after map loads
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    const m = mapRef.current;

    const effectLayers = [
      ...ALL_LAYERS,
      ...RADIUS_RING_LAYERS,
      // Site-band / site-layout layers (A1 only) live outside ALL_LAYERS so the
      // visibility loops never touch them, but they MUST be torn down here or the
      // re-run's addSource throws "source already exists" and blanks the page.
      ...SITE_BAND_LAYERS,
      ...SITE_ALT_LAYERS,
      ...SITE_PIECE_LAYERS,
      ...SITE_PAD_LAYERS,
      ...SITE_GHOST_LAYERS,
    ];
    // Teardown guarded: an exception here (e.g. a map mid-removal during dev HMR or
    // a rapid remount) must degrade to a console error, never a blank page.
    try {
      for (const id of effectLayers) if (m.getLayer(id)) m.removeLayer(id);
      for (const id of BRIEFING_EFFECT_SOURCES) if (m.getSource(id)) m.removeSource(id);
    } catch (e) {
      console.error('[BriefingMap] layer teardown failed:', e);
    }

    // Parcel boundary — for carve-out rep parcels, render the carve-out's
    // union polygon as the "main shape" so the IC reader sees the full
    // assembly footprint (e.g., Paces Creek Woodlands' 4-parcel block at
    // 2,356 ac per listing), not just one of the 4 parcels. Falls back to parcel.geojson
    // otherwise.
    const repCarveOut = (carveOuts || []).find(co =>
      co.geojson && (co.parcelIds || []).includes(parcel.id)
    );
    let parcelGeoJSON: GeoJSON.Geometry | null = null;
    if (repCarveOut) {
      parcelGeoJSON = repCarveOut.geojson as GeoJSON.Geometry;
    } else if (parcel.geojson) {
      parcelGeoJSON = typeof parcel.geojson === 'string'
        ? JSON.parse(parcel.geojson)
        : parcel.geojson;
    }

    m.addSource('briefing-parcel', {
      type: 'geojson',
      data: parcelGeoJSON
        ? { type: 'Feature', geometry: parcelGeoJSON, properties: {} }
        : EMPTY_FC,
    });
    m.addLayer({
      id: 'briefing-parcel-fill',
      type: 'fill',
      source: 'briefing-parcel',
      paint: { 'fill-color': '#ffffff', 'fill-opacity': 0.2 },
    });
    m.addLayer({
      id: 'briefing-parcel-outline',
      type: 'line',
      source: 'briefing-parcel',
      paint: { 'line-color': '#ffffff', 'line-width': 3, 'line-opacity': 0.9 },
    });

    // Carve-out polygon (proposed subdivision boundary, when this parcel is a
    // member of an operator_carve_outs record). Drawn under the parcel
    // outline so the briefed parcel still reads as the focus, but with a
    // distinctive sunrise-accent dashed border so the IC reader can see the
    // proposed multi-parcel boundary.
    // The carve-out we're already drawing as the main parcel polygon is excluded
    // here to avoid double-rendering the same shape with two different styles.
    const carveOutFC: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: (carveOuts || [])
        .filter(co => co.geojson && co.id !== repCarveOut?.id)
        .map(co => ({
          type: 'Feature',
          geometry: co.geojson as GeoJSON.Geometry,
          properties: { id: co.id, name: co.name },
        })),
    };
    m.addSource('briefing-carve-out', { type: 'geojson', data: carveOutFC });
    m.addLayer({
      id: 'briefing-carve-out-fill',
      type: 'fill',
      source: 'briefing-carve-out',
      paint: { 'fill-color': '#C4705A', 'fill-opacity': 0.12 },
    }, 'briefing-parcel-fill');
    m.addLayer({
      id: 'briefing-carve-out-outline',
      type: 'line',
      source: 'briefing-carve-out',
      paint: {
        'line-color': '#C4705A',
        'line-width': 2,
        'line-dasharray': [3, 2],
        'line-opacity': 0.85,
      },
    }, 'briefing-parcel-fill');

    // Annotations (empty initially)
    m.addSource('briefing-annotations', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'briefing-annotations-line',
      type: 'line',
      source: 'briefing-annotations',
      filter: ['==', '$type', 'LineString'],
      paint: { 'line-color': '#ffffff', 'line-width': 2, 'line-dasharray': [6, 4] },
      layout: { visibility: 'none' },
    });
    m.addLayer({
      id: 'briefing-annotations-label',
      type: 'symbol',
      source: 'briefing-annotations',
      filter: ['==', '$type', 'Point'],
      paint: { 'text-color': '#ffffff', 'text-halo-color': 'rgba(0,0,0,0.8)', 'text-halo-width': 1.5 },
      layout: {
        'text-field': ['get', 'label'],
        'text-size': 13,
        'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
        'text-allow-overlap': true,
        visibility: 'none',
      },
    });

    // Add pipeline and substation layers from bundled data (no separate fetch needed —
    // share-link viewers get map layers via the token-authenticated briefing API).
    function addLayerGroup(sourceId: string, data: GeoJSON.FeatureCollection, layers: Array<{ id: string; type: string; paint: any; layout?: any; filter?: any; minzoom?: number }>, sourceOpts: Record<string, unknown> = {}) {
      // Self-healing on a re-fire: a group whose source is not in BRIEFING_EFFECT_SOURCES
      // (the well and road groups) would otherwise throw "source already exists" and
      // blank the page.
      for (const layer of layers) if (m.getLayer(layer.id)) m.removeLayer(layer.id);
      if (m.getSource(sourceId)) m.removeSource(sourceId);
      m.addSource(sourceId, { type: 'geojson', data: data || EMPTY_FC, ...sourceOpts } as any);
      for (const layer of layers) {
        m.addLayer({ source: sourceId, ...layer } as any);
      }
    }

    // Western band (A1 only — key absent elsewhere). It is part of the site, so
    // it is painted in the assembly's family: the same white fill at the same
    // opacity as briefing-parcel-fill, and a SOLID white outline. No dashes —
    // dashes read as proposed/reference, and this is ground under the same
    // proposal as the core. It keeps a slightly lighter line weight than the
    // core outline (2.5 vs 3) so the core/band seam stays legible, plus the dark
    // casing that keeps a white line readable on satellite imagery. Default ON;
    // toggle chip below. Not in ALL_LAYERS, so the section-scroll visibility
    // loop never touches it.
    if (mapLayers.siteBand) {
      addLayerGroup('briefing-site-band', mapLayers.siteBand, [
        { id: 'briefing-site-band-fill', type: 'fill', paint: { 'fill-color': '#ffffff', 'fill-opacity': 0.2 }, layout: { visibility: 'visible' } },
        { id: 'briefing-site-band-casing', type: 'line', paint: { 'line-color': '#2E3840', 'line-width': 5, 'line-opacity': 0.55 }, layout: { visibility: 'visible' } },
        { id: 'briefing-site-band-outline', type: 'line', paint: { 'line-color': '#ffffff', 'line-width': 2.5, 'line-opacity': 0.85 }, layout: { visibility: 'visible' } },
        { id: 'briefing-site-band-label', type: 'symbol', layout: { visibility: padsVisibleRef.current ? 'none' : 'visible', 'text-field': ['get', 'label'], 'text-size': 12, 'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'], 'text-max-width': 9 }, paint: { 'text-color': '#F4ECD8', 'text-halo-color': '#2E3840', 'text-halo-width': 2 } },
      ]);
    }

    // Seller-proposed alternative layouts (A1 only). Painted in the same family
    // as the core and the band — white fill, dark casing, solid white outline —
    // because a selected alternative IS the site while it is on screen, not an
    // overlay on it. All four layers are filtered to the selected key. Under
    // them, the original premises (core + band) stays on screen as a dashed
    // ghost so the reader can see exactly what the seller moved.
    // Not in ALL_LAYERS: the section-scroll visibility loops never touch them.
    if (mapLayers.siteAlternatives) {
      const activeLayout = siteLayoutRef.current;
      const altVisibility: 'visible' | 'none' = activeLayout === 'original' ? 'none' : 'visible';
      const altFilter = ['==', ['get', 'key'], activeLayout];
      addLayerGroup('briefing-site-alt', mapLayers.siteAlternatives, [
        { id: 'briefing-site-alt-fill', type: 'fill', filter: altFilter, paint: { 'fill-color': '#ffffff', 'fill-opacity': 0.2 }, layout: { visibility: altVisibility } },
        { id: 'briefing-site-alt-casing', type: 'line', filter: altFilter, paint: { 'line-color': '#2E3840', 'line-width': 5, 'line-opacity': 0.55 }, layout: { visibility: altVisibility } },
        { id: 'briefing-site-alt-outline', type: 'line', filter: altFilter, paint: { 'line-color': '#ffffff', 'line-width': 3, 'line-opacity': 0.9 }, layout: { visibility: altVisibility } },
        { id: 'briefing-site-alt-label', type: 'symbol', filter: altFilter, layout: { visibility: altVisibility === 'visible' && !padsVisibleRef.current ? 'visible' : 'none', 'text-field': ['get', 'label'], 'text-size': 12, 'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'], 'text-max-width': 9 }, paint: { 'text-color': '#F4ECD8', 'text-halo-color': '#2E3840', 'text-halo-width': 2 } },
      ]);

      const ghostFeatures: GeoJSON.Feature[] = [];
      if (parcelGeoJSON) ghostFeatures.push({ type: 'Feature', geometry: parcelGeoJSON, properties: {} });
      for (const f of mapLayers.siteBand?.features || []) ghostFeatures.push(f);
      originalGhostRef.current = ghostFeatures;
      m.addSource('briefing-site-original-ghost', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: ghostFeatures },
      });
      m.addLayer({
        id: 'briefing-site-original-ghost-casing',
        type: 'line',
        source: 'briefing-site-original-ghost',
        paint: { 'line-color': '#2E3840', 'line-width': 3, 'line-opacity': 0.35 },
        layout: { visibility: altVisibility },
      });
      m.addLayer({
        id: 'briefing-site-original-ghost-outline',
        type: 'line',
        source: 'briefing-site-original-ghost',
        paint: { 'line-color': '#ffffff', 'line-width': 1.5, 'line-opacity': 0.6, 'line-dasharray': [2.5, 2] },
        layout: { visibility: altVisibility },
      });

      // Ghost tracts — where the selected alternative differs from the original.
      // Added pieces get a warm wash and a "+" label, dropped pieces a dark wash
      // and a "−" label; each label carries acres, upland and under-5 % slope.
      // Polygons and their label points share one source, split by `role`.
      if (mapLayers.siteAlternativePieces) {
        const pieceFilter = (role: 'area' | 'label') => ['all', ['==', ['get', 'parent'], activeLayout], ['==', ['get', 'role'], role]];
        const pieceVisibility: 'visible' | 'none' = altVisibility === 'visible' && piecesVisibleRef.current ? 'visible' : 'none';
        addLayerGroup('briefing-site-alt-pieces', mapLayers.siteAlternativePieces, [
          { id: 'briefing-site-alt-pieces-fill', type: 'fill', filter: pieceFilter('area'), paint: { 'fill-color': ['case', ['==', ['get', 'kind'], 'added'], '#F4ECD8', '#2E3840'], 'fill-opacity': ['case', ['==', ['get', 'kind'], 'added'], 0.22, 0.3] }, layout: { visibility: pieceVisibility } },
          { id: 'briefing-site-alt-pieces-outline', type: 'line', filter: pieceFilter('area'), paint: { 'line-color': ['case', ['==', ['get', 'kind'], 'added'], '#F4ECD8', '#8A9CAA'], 'line-width': 1.5, 'line-opacity': 0.9, 'line-dasharray': [2, 2] }, layout: { visibility: pieceVisibility } },
          { id: 'briefing-site-alt-pieces-label', type: 'symbol', filter: pieceFilter('label'), layout: { visibility: pieceVisibility, 'text-field': ['get', 'label'], 'text-size': 11, 'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'], 'text-max-width': 14, 'text-line-height': 1.25, 'text-allow-overlap': true }, paint: { 'text-color': '#F4ECD8', 'text-halo-color': '#2E3840', 'text-halo-width': 2 } },
        ]);
      }

      // Acreage boxes — the portfolio's dashed plan-view boxes for the layout on
      // screen, labelled with acres and the distance to the ET Texoma line.
      // Original / Alt 2 = clean rectangles; Alt 1 = engineered basis
      // (rectangles + irregular pads that cross intermittent channels —
      // `hazard_basis`, `kind`, `intermittent_ft_inside`). Filtered on `layout`.
      if (mapLayers.sitePads) {
        // Every rectangle for the layout on screen (best + runner-up per side, plus
        // the middle ground): the reader wants to see all the clean ground, not one box.
        const padVisibility: 'visible' | 'none' = padsVisibleRef.current ? 'visible' : 'none';
        addLayerGroup('briefing-site-pads', mapLayers.sitePads, [
          // No fill wash: the boxes are outlines only so the imagery reads through.
          // The fill layer stays (transparent) as the hover target.
          { id: 'briefing-site-pads-fill', type: 'fill', filter: buildSitePadFilter(activeLayout, 'area'), paint: { 'fill-color': '#e8b878', 'fill-opacity': 0 }, layout: { visibility: padVisibility } },
          // Gold (design-tokens pipelineGathering) — distinct from the peach used by
          // every gas line on this map, and unused elsewhere on the briefing map.
          { id: 'briefing-site-pads-line', type: 'line', filter: buildSitePadFilter(activeLayout, 'area'), paint: { 'line-color': '#e8b878', 'line-width': 2.4, 'line-dasharray': [3, 2], 'line-opacity': 0.95 }, layout: { visibility: padVisibility } },
          { id: 'briefing-site-pads-label', type: 'symbol', filter: buildSitePadFilter(activeLayout, 'label'), layout: { visibility: padVisibility, 'text-field': ['get', 'label'], 'text-size': 11, 'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'], 'text-line-height': 1.2, 'text-allow-overlap': true }, paint: { 'text-color': '#F4ECD8', 'text-halo-color': '#2E3840', 'text-halo-width': 2 } },
        ]);
      }

      // A re-run of this effect (carveOuts arriving late, a mapLayers change)
      // re-adds the core and band layers visible. If an alternative is active,
      // that would resurrect the original layout underneath it.
      if (activeLayout !== 'original') {
        for (const id of [...ORIGINAL_CORE_LAYERS, ...SITE_BAND_LAYERS]) {
          if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', 'none');
        }
      }
    }
    // Optional add-on tract (served only when the briefing's structured_intel
    // designates one — e.g. Cinco Frio's Boeck). Ghosted on purpose: dashed
    // faint outline + near-transparent fill so it reads as optional/reference,
    // in deliberate contrast to the solid outline of the controlled core.
    // Not in ALL_LAYERS — always visible, untouched by section-scroll loops.
    if (mapLayers.addOnTract) {
      addLayerGroup('briefing-add-on-tract', mapLayers.addOnTract, [
        { id: 'briefing-add-on-fill', type: 'fill', paint: { 'fill-color': '#ffffff', 'fill-opacity': 0.12 }, layout: { visibility: 'visible' } },
        { id: 'briefing-add-on-casing', type: 'line', paint: { 'line-color': '#2E3840', 'line-width': 4, 'line-opacity': 0.45 }, layout: { visibility: 'visible' } },
        { id: 'briefing-add-on-outline', type: 'line', paint: { 'line-color': '#ffffff', 'line-width': 2.2, 'line-opacity': 0.8, 'line-dasharray': [2.5, 2] }, layout: { visibility: 'visible' } },
        { id: 'briefing-add-on-label', type: 'symbol', layout: { visibility: 'visible', 'text-field': ['get', 'label'], 'text-size': 12, 'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'], 'text-max-width': 9 }, paint: { 'text-color': '#F4ECD8', 'text-halo-color': '#2E3840', 'text-halo-width': 2 } },
      ]);
    }
    // Gas transmission, bottom to top: the headline glow (the line the briefing's MW
    // figure is about — generalised from the old Desert Southwest hardcode), a dark
    // casing so the line reads over roads and caliche, the line itself with width by
    // diameter, and a name · diameter label from zoom 10.
    addLayerGroup('briefing-pipelines-gas', mapLayers.pipelinesGas, [
      { id: 'briefing-pipelines-gas-glow', type: 'line', filter: gasStyle.headlineFilter, paint: { 'line-color': '#ffa063', 'line-width': 14, 'line-opacity': 0.3, 'line-blur': 6 }, layout: { visibility: 'none' } },
      { id: 'briefing-pipelines-gas-casing', type: 'line', paint: { 'line-color': '#1E2530', 'line-width': gasStyle.casingWidth, 'line-opacity': 0.65 }, layout: { visibility: 'none', 'line-cap': 'round', 'line-join': 'round' } },
      { id: 'briefing-pipelines-gas', type: 'line', paint: { 'line-color': '#ffa063', 'line-width': gasStyle.width }, layout: { visibility: 'none', 'line-cap': 'round', 'line-join': 'round' } },
      { id: 'briefing-pipelines-gas-label', type: 'symbol', minzoom: 10, layout: {
        visibility: 'none', 'symbol-placement': 'line', 'symbol-spacing': 400,
        'text-field': gasStyle.labelText, 'text-size': 11, 'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
        'text-letter-spacing': 0.05, 'text-max-angle': 30, 'text-offset': [0, -0.9],
      }, paint: { 'text-color': '#F4ECD8', 'text-halo-color': '#1E2530', 'text-halo-width': 1.5 } },
    ]);
    addLayerGroup('briefing-pipelines-desert-sw', mapLayers.pipelinesDesertSw, [
      { id: 'briefing-pipelines-desert-sw-glow', type: 'line', paint: { 'line-color': '#C4705A', 'line-width': 14, 'line-opacity': 0.3, 'line-blur': 6 }, layout: { visibility: 'none' } },
      { id: 'briefing-pipelines-desert-sw', type: 'line', paint: { 'line-color': '#C4705A', 'line-width': 5 }, layout: { visibility: 'none' } },
    ]);
    addLayerGroup('briefing-pipelines-gathering', mapLayers.pipelinesGathering, [
      { id: 'briefing-pipelines-gathering', type: 'line', paint: { 'line-color': '#8A9CAA', 'line-width': 1.5, 'line-opacity': 0.7 }, layout: { visibility: 'none' } },
    ]);
    addLayerGroup('briefing-substations', mapLayers.substations, [
      { id: 'briefing-substations', type: 'circle', paint: { 'circle-color': '#C4705A', 'circle-radius': 5, 'circle-stroke-color': '#E4E6EA', 'circle-stroke-width': 1.5 }, layout: { visibility: 'none' } },
    ]);
    // FCC broadband hexes: a wash the imagery reads through. Off the constant map
    // (see FULL_VIEW_LAYERS); the Fiber section and the Fiber row/chip hover show them.
    addLayerGroup('briefing-fiber', mapLayers.fiberCoverage, [
      { id: 'briefing-fiber-fill', type: 'fill', paint: { 'fill-color': '#6A7E8E', 'fill-opacity': 0.35 }, layout: { visibility: 'none' } },
      { id: 'briefing-fiber-outline', type: 'line', paint: { 'line-color': '#506272', 'line-width': 1.5, 'line-opacity': 1 }, layout: { visibility: 'none' } },
    ]);
    addLayerGroup('briefing-aquifer', mapLayers.aquifers, [
      { id: 'briefing-aquifer-fill', type: 'fill', paint: { 'fill-color': '#6A7E8E', 'fill-opacity': 0.15 }, layout: { visibility: 'none' } },
      { id: 'briefing-aquifer-outline', type: 'line', paint: { 'line-color': '#6A7E8E', 'line-width': 1.5, 'line-opacity': 0.5 }, layout: { visibility: 'none' } },
    ]);
    addLayerGroup('briefing-highways', mapLayers.highways, [
      { id: 'briefing-highways', type: 'line', paint: { 'line-color': '#D8DCE0', 'line-width': 2, 'line-opacity': 0.7 }, layout: { visibility: 'none' } },
    ]);
    addLayerGroup('briefing-gas-plants', mapLayers.gasPlants, [
      { id: 'briefing-gas-plants', type: 'circle', paint: { 'circle-color': '#C4705A', 'circle-radius': 6, 'circle-stroke-color': '#E4E6EA', 'circle-stroke-width': 1.5, 'circle-opacity': 0.9 }, layout: { visibility: 'none' } },
    ]);
    addLayerGroup('briefing-compressors', mapLayers.compressors, [
      { id: 'briefing-compressors', type: 'circle', paint: { 'circle-color': '#D4915A', 'circle-radius': 7, 'circle-stroke-color': '#F5E8D6', 'circle-stroke-width': 2, 'circle-opacity': 1 }, layout: { visibility: 'none' } },
    ]);
    addLayerGroup('briefing-railroads', mapLayers.railroads, [
      { id: 'briefing-railroads', type: 'line', paint: { 'line-color': '#C8CCD0', 'line-width': 3, 'line-dasharray': [8, 4] }, layout: { visibility: 'none' } },
    ]);
    // Oil & gas wells (targeted RRC screen; keys absent on briefings without one → empty
    // sources, badge hidden). Laterals are the horizontal wells' surface→bottom-hole paths;
    // surface locations colour by status: producing amber, permitted hollow amber ring,
    // plugged / dry hole slate, shut-in terra-cotta.
    addLayerGroup('briefing-oil-gas-laterals', mapLayers.oilGasLaterals || EMPTY_FC, [
      { id: 'briefing-oil-gas-laterals', type: 'line', paint: { 'line-color': '#D4915A', 'line-width': 1.2, 'line-opacity': 0.7, 'line-dasharray': [2, 2] }, layout: { visibility: 'none' } },
    ]);
    addLayerGroup('briefing-oil-gas-wells', mapLayers.oilGasWells || EMPTY_FC, [
      { id: 'briefing-oil-gas-wells', type: 'circle', paint: {
        'circle-color': ['match', ['get', 'status'], 'producing', '#D4915A', 'shut_in', '#C4705A', 'permitted', '#1E2530', '#8A9CAA'],
        'circle-radius': 4.5,
        'circle-stroke-color': ['match', ['get', 'status'], 'permitted', '#D4915A', '#F5E8D6'],
        'circle-stroke-width': 1.5, 'circle-opacity': 1,
      }, layout: { visibility: 'none' } },
    ]);
    // Public roads (structured_intel.map_public_roads; key absent elsewhere → empty source,
    // badge hidden). TxDOT Roadway Inventory in the map box, drawn under the haul route and
    // frontage so those stay the loudest road lines; width steps down by class.
    addLayerGroup('briefing-public-roads', mapLayers.publicRoads || EMPTY_FC, [
      { id: 'briefing-public-roads', type: 'line', paint: {
        'line-color': '#C8CCD0',
        'line-width': ['match', ['get', 'hsys'], ['IH', 'US'], 2.2, ['SH', 'TL', 'SL', 'SS', 'BI', 'BU', 'BS', 'UA'], 1.8, ['FM', 'RM', 'PR'], 1.5, 1],
        'line-opacity': 0.75,
      }, layout: { visibility: 'none', 'line-cap': 'round', 'line-join': 'round' } },
    ]);
    // Road-access screen (targeted; keys absent elsewhere → empty sources, badges hidden).
    // The haul route from the interstate interchange gets a dark casing so a light line
    // reads on imagery; NBI structures (terra cotta), the gate (light, dark ring) and the
    // mapped ranch entrances (amber) ride the same source as points. Frontage = the
    // public segments along the boundary, solid amber; ranch tracks inside the fence dashed.
    addLayerGroup('briefing-access-route', mapLayers.accessRoute || EMPTY_FC, [
      { id: 'briefing-access-route-casing', type: 'line', filter: ['==', ['get', 'kind'], 'route'], paint: { 'line-color': '#1E2530', 'line-width': 6, 'line-opacity': 0.55 }, layout: { visibility: 'none', 'line-cap': 'round', 'line-join': 'round' } },
      { id: 'briefing-access-route', type: 'line', filter: ['==', ['get', 'kind'], 'route'], paint: { 'line-color': '#F5E8D6', 'line-width': 3, 'line-opacity': 1 }, layout: { visibility: 'none', 'line-cap': 'round', 'line-join': 'round' } },
      { id: 'briefing-access-points', type: 'circle', filter: ['==', ['geometry-type'], 'Point'], paint: {
        'circle-color': ['match', ['get', 'kind'], 'structure', '#C4705A', 'gate', '#F5E8D6', 'station', '#8A9CAA', '#D4915A'],
        'circle-radius': ['match', ['get', 'kind'], 'gate', 6, 'station', 4, 5],
        'circle-stroke-color': ['match', ['get', 'kind'], 'gate', '#1E2530', '#F5E8D6'],
        'circle-stroke-width': 1.5, 'circle-opacity': 1,
      }, layout: { visibility: 'none' } },
    ]);
    addLayerGroup('briefing-frontage-roads', mapLayers.frontageRoads || EMPTY_FC, [
      { id: 'briefing-frontage-roads', type: 'line', filter: ['==', ['get', 'kind'], 'frontage'], paint: { 'line-color': '#D4915A', 'line-width': 3.5, 'line-opacity': 0.95 }, layout: { visibility: 'none', 'line-cap': 'round', 'line-join': 'round' } },
      { id: 'briefing-frontage-tracks', type: 'line', filter: ['==', ['get', 'kind'], 'track'], paint: { 'line-color': '#C8CCD0', 'line-width': 1.5, 'line-opacity': 0.8, 'line-dasharray': [3, 2] }, layout: { visibility: 'none' } },
    ]);

    // Carrier fiber backbone routes
    addLayerGroup('briefing-carrier-fiber', mapLayers.carrierRoutes, [
      { id: 'briefing-carrier-fiber', type: 'line', paint: { 'line-color': '#4A90D9', 'line-width': 2.5, 'line-opacity': 1, 'line-dasharray': [6, 3] }, layout: { visibility: 'none' } },
    ]);
    // OSM switchyards (HIFLD-missing substations)
    addLayerGroup('briefing-osm-switchyards', mapLayers.osmSwitchyards, [
      { id: 'briefing-osm-switchyards', type: 'circle', paint: { 'circle-color': '#C4705A', 'circle-radius': 6, 'circle-stroke-color': '#2E3840', 'circle-stroke-width': 1.5, 'circle-opacity': 1 }, layout: { visibility: 'none' } },
    ]);
    // TWDB water wells
    addLayerGroup('briefing-water-wells', mapLayers.waterWells, [
      { id: 'briefing-water-wells', type: 'circle', paint: { 'circle-color': '#6A7E8E', 'circle-radius': 4, 'circle-stroke-color': '#E4E6EA', 'circle-stroke-width': 1 }, layout: { visibility: 'none' } },
    ]);
    // Nearby data centers
    addLayerGroup('briefing-data-centers', mapLayers.dataCenters, [
      { id: 'briefing-data-centers', type: 'circle', paint: { 'circle-color': '#3E4E5C', 'circle-radius': 7, 'circle-stroke-color': '#E4E6EA', 'circle-stroke-width': 2, 'circle-opacity': 1 }, layout: { visibility: 'none' } },
    ]);
    // Wetlands
    addLayerGroup('briefing-wetlands', mapLayers.wetlands, [
      { id: 'briefing-wetlands-fill', type: 'fill', paint: { 'fill-color': '#6A7E8E', 'fill-opacity': 0.25 }, layout: { visibility: 'none' } },
      { id: 'briefing-wetlands-outline', type: 'line', paint: { 'line-color': '#6A7E8E', 'line-width': 1, 'line-opacity': 0.5 }, layout: { visibility: 'none' } },
    ]);
    // FEMA 100-yr flood zones — terra-cotta to match the danger palette;
    // mid-saturation fill so it reads under the parcel outline without
    // competing with pipelines or substations. Filtered server-side to 100yr
    // only, so no need for a flood_type expression here.
    // TWDB screening polygons (source === 'twdb_screening', merged server-side for
    // no-FEMA parcels in targeted counties) render lighter with a dashed outline so
    // modeled screening reads distinct from regulatory FEMA. line-dasharray is not
    // data-drivable, hence the separate filtered outline layer.
    addLayerGroup('briefing-flood-zones', mapLayers.floodZones, [
      { id: 'briefing-flood-zones-fill', type: 'fill', paint: { 'fill-color': ['case', ['==', ['get', 'source'], 'fema_pending'], PENDING_FEMA_COLOR, '#C4705A'], 'fill-opacity': ['case', ['==', ['get', 'source'], 'twdb_screening'], 0.16, 0.25] }, layout: { visibility: 'none' } },
      { id: 'briefing-flood-zones-outline', type: 'line', filter: ['all', ['!=', ['get', 'source'], 'twdb_screening'], ['!=', ['get', 'source'], 'fema_pending']], paint: { 'line-color': '#C4705A', 'line-width': 1, 'line-opacity': 0.6 }, layout: { visibility: 'none' } },
      { id: 'briefing-flood-zones-twdb-outline', type: 'line', filter: ['==', ['get', 'source'], 'twdb_screening'], paint: { 'line-color': '#C4705A', 'line-width': 1.2, 'line-opacity': 0.8, 'line-dasharray': [2, 2] }, layout: { visibility: 'none' } },
      { id: 'briefing-flood-zones-pending-outline', type: 'line', filter: ['==', ['get', 'source'], 'fema_pending'], paint: { 'line-color': PENDING_FEMA_COLOR, 'line-width': 1.2, 'line-opacity': 0.85, 'line-dasharray': [3, 2] }, layout: { visibility: 'none' } },
    ]);
    // Airports
    addLayerGroup('briefing-airports', mapLayers.airports, [
      { id: 'briefing-airports', type: 'circle', paint: { 'circle-color': '#6A7E8E', 'circle-radius': 6, 'circle-stroke-color': '#E4E6EA', 'circle-stroke-width': 1.5, 'circle-opacity': 0.9 }, layout: { visibility: 'none' } },
    ]);

    // Gas supply radius rings (hover-triggered)
    m.addSource('briefing-radius-rings', { type: 'geojson', data: radiusRingsFC });

    // 10mi ring (outermost, lightest)
    m.addLayer({
      id: 'briefing-radius-10mi-fill', type: 'fill', source: 'briefing-radius-rings',
      filter: ['==', ['get', 'radius'], 10],
      paint: { 'fill-color': '#6A7E8E', 'fill-opacity': 0.15 },
      layout: { visibility: 'none' },
    });
    m.addLayer({
      id: 'briefing-radius-10mi-line', type: 'line', source: 'briefing-radius-rings',
      filter: ['==', ['get', 'radius'], 10],
      paint: { 'line-color': '#6A7E8E', 'line-width': 2, 'line-opacity': 0.7, 'line-dasharray': [6, 4] },
      layout: { visibility: 'none' },
    });

    // 5mi ring
    m.addLayer({
      id: 'briefing-radius-5mi-fill', type: 'fill', source: 'briefing-radius-rings',
      filter: ['==', ['get', 'radius'], 5],
      paint: { 'fill-color': '#6A7E8E', 'fill-opacity': 0.25 },
      layout: { visibility: 'none' },
    });
    m.addLayer({
      id: 'briefing-radius-5mi-line', type: 'line', source: 'briefing-radius-rings',
      filter: ['==', ['get', 'radius'], 5],
      paint: { 'line-color': '#6A7E8E', 'line-width': 2, 'line-opacity': 0.7, 'line-dasharray': [6, 4] },
      layout: { visibility: 'none' },
    });

    // 2mi ring (innermost, warmest)
    m.addLayer({
      id: 'briefing-radius-2mi-fill', type: 'fill', source: 'briefing-radius-rings',
      filter: ['==', ['get', 'radius'], 2],
      paint: { 'fill-color': '#C4705A', 'fill-opacity': 0.35 },
      layout: { visibility: 'none' },
    });
    m.addLayer({
      id: 'briefing-radius-2mi-line', type: 'line', source: 'briefing-radius-rings',
      filter: ['==', ['get', 'radius'], 2],
      paint: { 'line-color': '#C4705A', 'line-width': 2.5, 'line-opacity': 0.8, 'line-dasharray': [6, 4] },
      layout: { visibility: 'none' },
    });

    // Move parcel outline above rings so it's always visible
    if (m.getLayer('briefing-parcel-fill')) m.moveLayer('briefing-parcel-fill');
    if (m.getLayer('briefing-parcel-outline')) m.moveLayer('briefing-parcel-outline');
    // Ring labels
    m.addLayer({
      id: 'briefing-radius-labels', type: 'symbol', source: 'briefing-radius-rings',
      filter: ['==', '$type', 'Point'],
      paint: { 'text-color': '#ffffff', 'text-halo-color': 'rgba(0,0,0,0.7)', 'text-halo-width': 1.5 },
      layout: {
        'text-field': ['get', 'label'],
        'text-size': 12,
        'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
        'text-allow-overlap': true,
        visibility: 'none',
      },
    });
    // The alternative-layout stack goes above everything, ring labels
    // included: ghost first, then the alt polygon, so the alt label is topmost.
    for (const id of [...SITE_GHOST_LAYERS, 'briefing-site-alt-fill', 'briefing-site-alt-casing', 'briefing-site-alt-outline', ...SITE_PIECE_LAYERS, ...SITE_PAD_LAYERS, 'briefing-site-alt-label']) {
      if (m.getLayer(id)) m.moveLayer(id);
    }
    // The measure tool's own layers (added by the hook, not this effect) stay topmost.
    for (const id of ['measurement-area-fill', 'measurement-area-line', 'measurement-lines', 'measurement-preview', 'measurement-points']) {
      if (m.getLayer(id)) m.moveLayer(id);
    }

    // This effect re-runs whenever a dep changes identity — a refetch swaps
    // mapLayers (lens toggle; StrictMode's duplicate fetches in dev), carveOuts
    // can arrive late — and every layer above was just re-added HIDDEN. The
    // view-state effects below do not re-run on a rebuild, so without this the
    // Full View / the active section's layers vanish until the reader scrolls.
    // Re-apply the current view state here; the camera is deliberately untouched.
    if (isFullView || briefingMode === 'checklist') {
      for (const layerId of FULL_VIEW_LAYERS) {
        if (!m.getLayer(layerId)) continue;
        m.setLayoutProperty(layerId, 'visibility', 'visible');
        const overrides = FULL_VIEW_OPACITY_OVERRIDES[layerId];
        if (overrides) {
          for (const [prop, val] of Object.entries(overrides)) m.setPaintProperty(layerId, prop as any, val);
        }
      }
      for (const layerId of RADIUS_RING_LAYERS) {
        if (m.getLayer(layerId)) m.setLayoutProperty(layerId, 'visibility', padsVisibleRef.current ? 'none' : 'visible');
      }
    } else {
      const sectionState = SECTION_MAP_STATES[effectiveSection];
      for (const layerId of sectionState.layers) {
        if (m.getLayer(layerId)) m.setLayoutProperty(layerId, 'visibility', 'visible');
      }
      // The annotation source was rebuilt empty above; restore the section's
      // callout the same way the scroll-sync effect builds it.
      const annSrc = m.getSource('briefing-annotations') as mapboxgl.GeoJSONSource | undefined;
      const annotations = (sectionState as { annotations?: 'pipeline' | 'substation' }).annotations;
      if (annSrc && annotations && m.getLayer('briefing-annotations-line') && m.getLayer('briefing-annotations-label')) {
        if (annotations === 'pipeline') buildPipelineAnnotation(parcel, pipelines, annSrc, mapLayers);
        else buildSubstationAnnotation(parcel, annSrc, mapLayers);
        m.setLayoutProperty('briefing-annotations-line', 'visibility', 'visible');
        m.setLayoutProperty('briefing-annotations-label', 'visibility', 'visible');
      }
    }

    const allClickable: string[] = [...CLICKABLE_PIPELINE_LAYERS, ...CLICKABLE_FEATURE_LAYERS];
    const onClick = (e: mapboxgl.MapMouseEvent) => {
      if (measureActiveRef.current) return;
      const visible = allClickable.filter(id => m.getLayer(id));
      const features = m.queryRenderedFeatures(e.point, { layers: visible });
      if (!features.length) return;
      // Prefer the most precise rendered hit when several layers overlap.
      const feature = features.find(f => f.layer?.type === 'circle')
        || features.find(f => f.layer?.type === 'line')
        || features[0];
      if (layerControlsRef.current?.selectFeature(feature, e.lngLat)) {
        popupRef.current?.remove();
        popupRef.current = null;
        return;
      }
      layerControlsRef.current?.closePopup();
      const layerId = feature.layer?.id;
      if (!layerId) return;
      const props = feature.properties || {};

      let html: string;
      if ((CLICKABLE_PIPELINE_LAYERS as readonly string[]).includes(layerId)) {
        html = buildPipelinePopupHTML(props as any);
      } else {
        const dist = haversineMi(Number(parcel.lat), Number(parcel.lng), e.lngLat.lat, e.lngLat.lng);
        html = buildFeaturePopupHTML(layerId, props, dist);
      }

      if (popupRef.current) popupRef.current.remove();
      popupRef.current = new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: true,
        maxWidth: '280px',
        className: 'briefing-pipeline-popup',
        offset: [0, -10],
      })
        .setLngLat(e.lngLat)
        .setHTML(html)
        .addTo(m);
    };
    m.on('click', onClick);

    let padPopup: mapboxgl.Popup | null = null;
    const onPadMove = (e: mapboxgl.MapLayerMouseEvent) => {
      if (measureActiveRef.current) return;
      const p = e.features?.[0]?.properties || {};
      const fmt = (v: unknown) => Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 });
      const html = `<div style="font-family:'DIN Pro',Arial,sans-serif;font-size:11px;line-height:1.45;color:#2E3840">`
        + `<div style="font-size:8.5px;letter-spacing:0.06em;color:#788894;text-transform:uppercase">${p.basis_status === 'illustrative-acreage' ? 'Illustrative acreage area' : p.kind === 'irregular' ? 'Acreage area · irregular, follows the clean ground' : 'Acreage box'}${p.basis_status === 'illustrative-acreage' ? '' : p.hazard_basis === 'engineered' ? ' · engineered basis' : ` · ${p.side} ground${Number(p.rank) > 1 ? ' · second rectangle' : ''}`}</div>`
        + `<div style="font-weight:700">${fmt(p.acres)} ac · ${Number(p.texoma_mi).toFixed(2)} mi to the ET Texoma line</div>`
        + `<div style="color:#6A7E8E">${p.long_ft ? `${fmt(p.long_ft)} × ${fmt(p.short_ft)} ft · ` : ''}${p.slope_mean_pct != null ? (p.slope_mean_pct_5m != null
          ? `${meanRange(Number(p.slope_mean_pct), Number(p.slope_mean_pct_5m))} mean slope · ${acRange(Number(p.acres_under_5), Number(p.acres_under_5_5m))} ac under 5% (3–5 m grids)`
          : `${Number(p.slope_mean_pct).toFixed(1)}% mean slope · ${fmt(p.acres_under_5)} ac under 5%`) : ''}</div>`
        + (p.terrain_status === 'reference' ? '<div style="color:#6A7E8E">Terrain: retained acreage-area screening</div>' : '')
        + (p.hazard_basis === 'engineered'
          ? `<div style="color:#6A7E8E">${p.basis_status === 'illustrative-acreage' ? 'Retained area screening: ' : ''}avoids 100-yr flood · forested wetland · Shawnee Creek${Number(p.setback100ft_acres) > 0 ? ` · ${fmt(p.setback100ft_acres)} ac with a 100-ft setback` : ''}</div>`
            + (Number(p.intermittent_ft_inside) > 0
              ? `<div style="color:#8A302A">Crosses ${fmt(p.intermittent_ft_inside)} ft of intermittent channel${Number(p.riverine_wetland_ac_inside) > 0 ? ` · ${Number(p.riverine_wetland_ac_inside).toFixed(1)} ac riverine NWI` : ''} — Section 404 / stormwater question</div>`
              : `<div style="color:#6A7E8E">no mapped channel inside</div>`)
          : `<div style="color:#6A7E8E">avoids streams · wetlands · 100-yr flood${Number(p.setback100ft_acres) > 0 ? ` · ${fmt(p.setback100ft_acres)} ac with a 100-ft setback` : ''}</div>`)
        + (Number(p.texoma_ft_inside) > 0 || Number(p.other_pipeline_ft_inside) > 0
          ? `<div style="color:#8A302A">Existing pipeline inside: ${Number(p.texoma_ft_inside) > 0 ? `${fmt(p.texoma_ft_inside)} ft of the Texoma 30″` : ''}${Number(p.texoma_ft_inside) > 0 && Number(p.other_pipeline_ft_inside) > 0 ? ' · ' : ''}${Number(p.other_pipeline_ft_inside) > 0 ? `${fmt(p.other_pipeline_ft_inside)} ft of another line` : ''}</div>`
          : '')
        + `</div>`;
      if (!padPopup) padPopup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, maxWidth: '280px', className: 'briefing-pipeline-popup', offset: 12 });
      padPopup.setLngLat(e.lngLat).setHTML(html).addTo(m);
      m.getCanvas().style.cursor = 'default';
    };
    const onPadLeave = () => { if (padPopup) padPopup.remove(); m.getCanvas().style.cursor = ''; };
    if (m.getLayer('briefing-site-pads-fill')) {
      m.on('mousemove', 'briefing-site-pads-fill', onPadMove);
      m.on('mouseleave', 'briefing-site-pads-fill', onPadLeave);
    }

    const hoverHandlers: Array<{ layerId: string; enter: () => void; leave: () => void }> = [];
    for (const layerId of allClickable) {
      const enter = () => { m.getCanvas().style.cursor = 'pointer'; };
      const leave = () => { m.getCanvas().style.cursor = ''; };
      hoverHandlers.push({ layerId, enter, leave });
      m.on('mouseenter', layerId, enter);
      m.on('mouseleave', layerId, leave);
    }

    return () => {
      // Cleanup guarded: Mapbox throws when the map or style is already torn down
      // (StrictMode double-mount, HMR); a cleanup exception blanks the whole page.
      try {
        m.off('click', onClick);
        m.off('mousemove', 'briefing-site-pads-fill', onPadMove);
        m.off('mouseleave', 'briefing-site-pads-fill', onPadLeave);
        if (padPopup) padPopup.remove();
        for (const h of hoverHandlers) {
          m.off('mouseenter', h.layerId, h.enter);
          m.off('mouseleave', h.layerId, h.leave);
        }
        for (const id of effectLayers) if (m.getLayer(id)) m.removeLayer(id);
        for (const id of BRIEFING_EFFECT_SOURCES) if (m.getSource(id)) m.removeSource(id);
      } catch (e) {
        console.error('[BriefingMap] layer cleanup failed:', e);
      }
    };
  }, [mapLoaded, parcel.geojson, parcel.id, carveOuts, mapLayers, radiusRingsFC]); // eslint-disable-line react-hooks/exhaustive-deps

  // SITE LAYOUT switch (A1 only). Owns the visibility of the original premises
  // (core + carve-out + band) against a selected alternative, and re-frames the
  // camera on the union of the two so the reader sees both at once.
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !mapLayers.siteAlternatives) return;
    const m = mapRef.current;
    if (!m.getLayer('briefing-site-alt-fill')) return;

    const setVisibility = (ids: readonly string[], visibility: 'visible' | 'none') => {
      for (const id of ids) if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', visibility as 'visible' | 'none');
    };

    const isAlt = siteLayout !== 'original';
    for (const id of SITE_PAD_LAYERS) {
      if (m.getLayer(id)) m.setFilter(id, buildSitePadFilter(siteLayout, id.endsWith('-label') ? 'label' : 'area') as any);
    }
    setVisibility(SITE_PAD_LAYERS, padsVisible ? 'visible' : 'none');
    if (isAlt) {
      for (const id of SITE_ALT_LAYERS) {
        if (m.getLayer(id)) m.setFilter(id, ['==', ['get', 'key'], siteLayout] as any);
      }
      for (const id of SITE_PIECE_LAYERS) {
        if (m.getLayer(id)) m.setFilter(id, ['all', ['==', ['get', 'parent'], siteLayout], ['==', ['get', 'role'], id.endsWith('-label') ? 'label' : 'area']] as any);
      }
      setVisibility(ORIGINAL_CORE_LAYERS, 'none');
      setVisibility(SITE_BAND_LAYERS, 'none');
      setVisibility(SITE_ALT_LAYERS, 'visible');
      setVisibility(SITE_PIECE_LAYERS, piecesVisible ? 'visible' : 'none');
      setVisibility(SITE_GHOST_LAYERS, 'visible');
    } else {
      setVisibility(SITE_ALT_LAYERS, 'none');
      setVisibility(SITE_PIECE_LAYERS, 'none');
      setVisibility(SITE_GHOST_LAYERS, 'none');
      // Only the parcel pair comes back here; the carve-out pair stays with the
      // section-scroll loop, which hides it on every section change anyway.
      setVisibility(['briefing-parcel-fill', 'briefing-parcel-outline'], 'visible');
      setVisibility(SITE_BAND_LAYERS, accessTractVisible ? 'visible' : 'none');
    }
    // With ACREAGE BOXES on, the outline labels ("Alternative #1 · ±3,447 ac", the
    // western-band label) sit in the middle of the pads and collide with the
    // pad labels — suppress them; they come back when the pads go off.
    if (padsVisible) setVisibility(['briefing-site-alt-label', 'briefing-site-band-label'], 'none');

    // Camera moves only on an actual layout change — never on the first run at
    // the default layout, and never when this effect re-fires for the WESTERN
    // BAND toggle.
    if (cameraLayoutRef.current === siteLayout) return;
    const firstRun = cameraLayoutRef.current === null;
    cameraLayoutRef.current = siteLayout;
    if (firstRun && !isAlt) return;

    const geometries: GeoJSON.Geometry[] = originalGhostRef.current.map(f => f.geometry);
    if (isAlt) {
      const alt = mapLayers.siteAlternatives.features.find(f => f.properties?.key === siteLayout);
      if (alt) geometries.push(alt.geometry);
    }
    const bounds = geometryBounds(geometries);
    if (bounds) {
      disarmMeasurements();
      m.fitBounds(bounds, { padding: { top: 70, bottom: 60, left: 70, right: 60 }, duration: 900 });
    }
  }, [siteLayout, mapLoaded, accessTractVisible, piecesVisible, padsVisible, disarmMeasurements]);

  // Scroll-sync: respond to section changes (or mode changes)
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    if (effectiveSection === prevSectionRef.current && briefingMode === 'brief') return;
    prevSectionRef.current = effectiveSection;
    if (isFullView && briefingMode === 'brief') return;
    // Checklist mode is handled by the Full View effect below — show all populated layers, fly to zoom 10.
    if (briefingMode === 'checklist') return;

    const m = mapRef.current;
    const sectionState = SECTION_MAP_STATES[effectiveSection];
    // Cancel in-flight animation
    disarmMeasurements();
    m.stop();

    // Set layer visibilities (skip layers not yet added). An active alternative
    // layout owns the core's visibility — a section change must not resurrect
    // the original premises underneath it.
    const originalLayoutActive = !mapLayers.siteAlternatives || siteLayoutRef.current === 'original';
    for (const layerId of ALL_LAYERS) {
      if (!m.getLayer(layerId)) continue;
      const visible = sectionState.layers.includes(layerId)
        || (originalLayoutActive && (layerId === 'briefing-parcel-fill' || layerId === 'briefing-parcel-outline'));
      m.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
    }

    // Build annotations
    const annotationSource = m.getSource('briefing-annotations') as mapboxgl.GeoJSONSource | undefined;
    if (annotationSource) {
      const hasAnnotationLayers = m.getLayer('briefing-annotations-line') && m.getLayer('briefing-annotations-label');
      if (sectionState.annotations === 'pipeline') {
        buildPipelineAnnotation(parcel, pipelines, annotationSource, mapLayers);
        if (hasAnnotationLayers) {
          m.setLayoutProperty('briefing-annotations-line', 'visibility', 'visible');
          m.setLayoutProperty('briefing-annotations-label', 'visibility', 'visible');
        }
      } else if (sectionState.annotations === 'substation') {
        buildSubstationAnnotation(parcel, annotationSource, mapLayers);
        if (hasAnnotationLayers) {
          m.setLayoutProperty('briefing-annotations-line', 'visibility', 'visible');
          m.setLayoutProperty('briefing-annotations-label', 'visibility', 'visible');
        }
      } else {
        annotationSource.setData(EMPTY_FC);
        if (hasAnnotationLayers) {
          m.setLayoutProperty('briefing-annotations-line', 'visibility', 'none');
          m.setLayoutProperty('briefing-annotations-label', 'visibility', 'none');
        }
      }
    }

    // Fly to appropriate zoom
    m.flyTo({
      center: [Number(parcel.lng), Number(parcel.lat)],
      zoom: sectionState.zoom ?? 9,
      duration: 1200,
      essential: true,
    });
  }, [effectiveSection, activeSection, isFullView, briefingMode, mapLoaded, parcel, pipelines, disarmMeasurements, mapLayers]);

  // Full View / Checklist: activate all layers + distance rings, zoom to site
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    const m = mapRef.current;

    const showAll = isFullView || briefingMode === 'checklist';
    disarmMeasurements();
    if (showAll) {
      for (const layerId of FULL_VIEW_LAYERS) {
        if (!m.getLayer(layerId)) continue;
        m.setLayoutProperty(layerId, 'visibility', 'visible');
        const overrides = FULL_VIEW_OPACITY_OVERRIDES[layerId];
        if (overrides) {
          for (const [prop, val] of Object.entries(overrides)) {
            m.setPaintProperty(layerId, prop, val);
          }
        }
      }
      // The radius rings stay off while ACREAGE BOXES is on — their wash sits
      // right under the pads and buries the imagery the pads are drawn on.
      for (const layerId of RADIUS_RING_LAYERS) {
        if (m.getLayer(layerId)) m.setLayoutProperty(layerId, 'visibility', padsVisibleRef.current ? 'none' : 'visible');
      }
      // The A1 western band extends ~1.3 mi west; nudge the full-view center west
      // and pull the zoom out slightly so the core + the band + the ET Texoma
      // line frame together (A1 only — layer absent elsewhere).
      const accessPresent = !!mapLayers.siteBand;
      m.flyTo({
        center: [Number(parcel.lng) - (accessPresent ? 0.018 : 0), Number(parcel.lat)],
        zoom: accessPresent ? 10.2 : 10.5,
        duration: 1200,
        essential: true,
      });
    } else {
      // Revert to Guided: hide Full View layers, restore defaults, re-apply scroll-sync state
      for (const layerId of FULL_VIEW_LAYERS) {
        if (!m.getLayer(layerId)) continue;
        m.setLayoutProperty(layerId, 'visibility', 'none');
        const defaults = DEFAULT_PAINT[layerId];
        if (defaults) {
          for (const [prop, val] of Object.entries(defaults)) {
            m.setPaintProperty(layerId, prop, val);
          }
        }
      }
      for (const layerId of RADIUS_RING_LAYERS) {
        if (m.getLayer(layerId)) m.setLayoutProperty(layerId, 'visibility', 'none');
      }
      // Re-apply current section's layer visibility
      const sectionState = SECTION_MAP_STATES[activeSection];
      for (const layerId of sectionState.layers) {
        if (m.getLayer(layerId)) m.setLayoutProperty(layerId, 'visibility', 'visible');
      }
      // Keep parcel always visible — unless an alternative layout is active, in
      // which case the switcher owns the core's visibility.
      if (!mapLayers.siteAlternatives || siteLayoutRef.current === 'original') {
        if (m.getLayer('briefing-parcel-fill')) m.setLayoutProperty('briefing-parcel-fill', 'visibility', 'visible');
        if (m.getLayer('briefing-parcel-outline')) m.setLayoutProperty('briefing-parcel-outline', 'visibility', 'visible');
      }
      m.flyTo({
        center: [Number(parcel.lng), Number(parcel.lat)],
        zoom: sectionState.zoom ?? 12,
        duration: 1200,
        essential: true,
      });
    }
  }, [isFullView, briefingMode, mapLoaded, parcel.lng, parcel.lat, disarmMeasurements, mapLayers]);

  // Hover highlight: when a feature is hovered in the content, pulse it on the map
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    const m = mapRef.current;

    // Reset all layers to defaults from shared DEFAULT_PAINT table
    for (const [id, defaults] of Object.entries(DEFAULT_PAINT)) {
      if (!m.getLayer(id)) continue;
      for (const [prop, val] of Object.entries(defaults)) {
        m.setPaintProperty(id, prop, val);
      }
    }
    highlightLayer(null); // Reset any badge-style emphasis
    if (hoverPopupRef.current) { hoverPopupRef.current.remove(); hoverPopupRef.current = null; }

    if (!hoverFeature) {
      // Clear hover-triggered annotations and overlays
      const annSrc = m.getSource('briefing-annotations') as mapboxgl.GeoJSONSource | undefined;
      if (annSrc) annSrc.setData(EMPTY_FC);
      if (m.getLayer('briefing-annotations-line')) m.setLayoutProperty('briefing-annotations-line', 'visibility', 'none');
      if (m.getLayer('briefing-annotations-label')) m.setLayoutProperty('briefing-annotations-label', 'visibility', 'none');
      // Hide hover-only overlays (aquifer, radius rings). In Full View mode, keep
      // radius rings and aquifer visible.
      if (!isFullView) {
        if (m.getLayer('briefing-aquifer-fill')) m.setLayoutProperty('briefing-aquifer-fill', 'visibility', 'none');
        if (m.getLayer('briefing-aquifer-outline')) m.setLayoutProperty('briefing-aquifer-outline', 'visibility', 'none');
        for (const layerId of RADIUS_RING_LAYERS) {
          if (m.getLayer(layerId)) m.setLayoutProperty(layerId, 'visibility', 'none');
        }
      }
      return;
    }

    if (hoverFeature.type === 'pipeline') {
      const isDesert = isDesertSW(hoverFeature.name);

      if (isDesert) {
        if (m.getLayer('briefing-pipelines-desert-sw')) m.setPaintProperty('briefing-pipelines-desert-sw', 'line-width', 12);
        if (m.getLayer('briefing-pipelines-desert-sw-glow')) {
          m.setPaintProperty('briefing-pipelines-desert-sw-glow', 'line-opacity', 0.6);
          m.setPaintProperty('briefing-pipelines-desert-sw-glow', 'line-width', 24);
        }
        // Other layers fully dim — Desert SW is always its own match.
        if (m.getLayer('briefing-pipelines-gas')) m.setPaintProperty('briefing-pipelines-gas', 'line-opacity', 0.2);
        if (m.getLayer('briefing-pipelines-gas-casing')) m.setPaintProperty('briefing-pipelines-gas-casing', 'line-opacity', 0.2);
        if (m.getLayer('briefing-pipelines-gas-glow')) m.setPaintProperty('briefing-pipelines-gas-glow', 'line-opacity', 0.1);
        if (m.getLayer('briefing-pipelines-gathering')) m.setPaintProperty('briefing-pipelines-gathering', 'line-opacity', 0.15);
      } else {
        // Per-line emphasis when the hovered row's segment keys (name|operator pairs)
        // resolve against the map layer's features — verified against the payload FC
        // BEFORE dimming anything, so a mismatch (the historical "TGP 100" class) can
        // never dim everything to nothing. Wholesale brighten stays the fallback.
        const hoverKeys: string[] = Array.isArray((hoverFeature as any).segmentKeys) ? (hoverFeature as any).segmentKeys : [];
        const featKey = (p: any) => `${p?.name || ''}|${p?.operator || ''}`;
        const gasFeats = mapLayers.pipelinesGas?.features || [];
        const gatherFeats = mapLayers.pipelinesGathering?.features || [];
        const matchedFeat = hoverKeys.length
          ? gasFeats.find(f => hoverKeys.includes(featKey(f.properties))) || gatherFeats.find(f => hoverKeys.includes(featKey(f.properties)))
          : undefined;
        if (matchedFeat) {
          const matchExpr = ['in', ['concat', ['coalesce', ['get', 'name'], ''], '|', ['coalesce', ['get', 'operator'], '']], ['literal', hoverKeys]] as any;
          if (m.getLayer('briefing-pipelines-gas')) {
            m.setPaintProperty('briefing-pipelines-gas', 'line-width', ['case', matchExpr, 6, gasStyle.width] as any);
            m.setPaintProperty('briefing-pipelines-gas', 'line-opacity', ['case', matchExpr, 1, 0.3] as any);
          }
          if (m.getLayer('briefing-pipelines-gas-casing')) {
            m.setPaintProperty('briefing-pipelines-gas-casing', 'line-width', ['case', matchExpr, 8.5, gasStyle.casingWidth] as any);
            m.setPaintProperty('briefing-pipelines-gas-casing', 'line-opacity', ['case', matchExpr, 0.8, 0.25] as any);
          }
          if (m.getLayer('briefing-pipelines-gas-glow')) {
            m.setPaintProperty('briefing-pipelines-gas-glow', 'line-width', ['case', matchExpr, 22, 14] as any);
            m.setPaintProperty('briefing-pipelines-gas-glow', 'line-opacity', ['case', matchExpr, 0.55, 0.12] as any);
          }
          if (m.getLayer('briefing-pipelines-gathering')) {
            m.setPaintProperty('briefing-pipelines-gathering', 'line-width', ['case', matchExpr, 4, 1.5] as any);
            m.setPaintProperty('briefing-pipelines-gathering', 'line-opacity', ['case', matchExpr, 0.95, 0.25] as any);
          }
          if (m.getLayer('briefing-pipelines-desert-sw')) m.setPaintProperty('briefing-pipelines-desert-sw', 'line-opacity', 0.4);
          // Card at the matched line's closest approach to the site.
          const pt = nearestPointOnGeometry(matchedFeat.geometry, [Number(parcel.lng), Number(parcel.lat)]);
          if (pt) {
            hoverPopupRef.current = new mapboxgl.Popup({
              closeButton: false, closeOnClick: false, maxWidth: '280px',
              className: 'briefing-pipeline-popup', offset: [0, -10],
            })
              .setLngLat(pt)
              .setHTML(buildPipelinePopupHTML(matchedFeat.properties as any))
              .addTo(m);
          }
        } else {
          if (m.getLayer('briefing-pipelines-gas')) {
            m.setPaintProperty('briefing-pipelines-gas', 'line-width', 5);
            m.setPaintProperty('briefing-pipelines-gas', 'line-opacity', 1);
          }
          if (m.getLayer('briefing-pipelines-gas-casing')) {
            m.setPaintProperty('briefing-pipelines-gas-casing', 'line-width', 7.5);
            m.setPaintProperty('briefing-pipelines-gas-casing', 'line-opacity', 0.8);
          }
          if (m.getLayer('briefing-pipelines-gathering')) {
            m.setPaintProperty('briefing-pipelines-gathering', 'line-width', 3);
            m.setPaintProperty('briefing-pipelines-gathering', 'line-opacity', 0.95);
          }
          if (m.getLayer('briefing-pipelines-desert-sw')) m.setPaintProperty('briefing-pipelines-desert-sw', 'line-opacity', 0.4);
        }
      }
      // Make sure pipeline layers are visible
      if (m.getLayer('briefing-pipelines-desert-sw')) m.setLayoutProperty('briefing-pipelines-desert-sw', 'visibility', 'visible');
      if (m.getLayer('briefing-pipelines-desert-sw-glow')) m.setLayoutProperty('briefing-pipelines-desert-sw-glow', 'visibility', 'visible');
      for (const id of ['briefing-pipelines-gas-glow', 'briefing-pipelines-gas-casing', 'briefing-pipelines-gas', 'briefing-pipelines-gas-label', 'briefing-pipelines-gathering']) {
        if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', 'visible');
      }
    } else if (hoverFeature.type === 'substation') {
      highlightLayer('briefing-substations');
      if (m.getLayer('briefing-substations')) m.setLayoutProperty('briefing-substations', 'visibility', 'visible');
    } else if (hoverFeature.type === 'airport') {
      // Distant major airports surface via PiP (handled in the PiP useEffect).
      // Any local airport circles in the viewport get a paint emphasis.
      highlightLayer('briefing-airports');
      if (m.getLayer('briefing-airports')) m.setLayoutProperty('briefing-airports', 'visibility', 'visible');
    } else if (hoverFeature.type === 'compressor') {
      // Mirror substation/airport: emphasize the compressor layer paint and ensure visible.
      highlightLayer('briefing-compressors');
      if (m.getLayer('briefing-compressors')) m.setLayoutProperty('briefing-compressors', 'visibility', 'visible');
    } else if (hoverFeature.type === 'highway' || hoverFeature.type === 'gas-plant' || hoverFeature.type === 'railroad' || hoverFeature.type === 'fiber') {
      // Map content hover type to badge layer ID for consistent emphasis
      const badgeMap: Record<string, string> = {
        'highway': 'briefing-highways',
        'gas-plant': 'briefing-gas-plants',
        'railroad': 'briefing-railroads',
        'fiber': 'briefing-carrier-fiber',
      };
      const badgeId = badgeMap[hoverFeature.type];
      if (badgeId) highlightLayer(badgeId);

      // Make sure the layer is visible (in Guided mode it might be hidden)
      const layerGroup = LAYER_GROUP[badgeId];
      if (layerGroup) {
        for (const id of layerGroup) {
          if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', 'visible');
        }
      }

      // Show distance annotation whenever the FC has features. Both the line and the label come from the
      // matched feature so they stay consistent — when the carve-out centroid override puts the map far
      // from the rep parcel (e.g. Paces), the precomputed nearest-X columns no longer match what's
      // visually closest, so we recompute name + miles from the picked feature.
      const annotationSource = m.getSource('briefing-annotations') as mapboxgl.GeoJSONSource | undefined;
      if (annotationSource) {
        const center: [number, number] = [Number(parcel.lng), Number(parcel.lat)];
        const c = hoverConfig[hoverFeature.type];
        if (c?.miles != null) {
          let closestPt: [number, number] | null = null;
          let closestDist = Infinity;
          let closestFeat: GeoJSON.Feature | null = null;
          if (c.fc?.features?.length) {
            for (const f of c.fc.features) {
              const pt = nearestPointOnGeometry(f.geometry, center);
              if (pt) {
                const d = Math.hypot(pt[0] - center[0], pt[1] - center[1]);
                if (d < closestDist) { closestDist = d; closestPt = pt; closestFeat = f; }
              }
            }
          }
          if (closestPt && closestFeat) {
            const featProps = (closestFeat.properties || {}) as Record<string, unknown>;
            const featName = (hoverFeature.type === 'fiber' && typeof featProps.carrier === 'string' && featProps.carrier) ||
                             (typeof featProps.name === 'string' && featProps.name) ||
                             (typeof featProps.operator === 'string' && featProps.operator) ||
                             c.name;
            const featMiles = haversineMi(Number(parcel.lat), Number(parcel.lng), closestPt[1], closestPt[0]);
            const midpoint: [number, number] = [(center[0] + closestPt[0]) / 2, (center[1] + closestPt[1]) / 2];
            const label = `${featName} — ${featMiles.toFixed(1)} mi${hoverFeature.type === 'fiber' ? ' from representative parcel centroid' : ''}`;
            annotationSource.setData({
              type: 'FeatureCollection',
              features: [
                { type: 'Feature', geometry: { type: 'LineString', coordinates: [center, closestPt] }, properties: {} },
                { type: 'Feature', geometry: { type: 'Point', coordinates: midpoint }, properties: { label } },
              ],
            });
            if (m.getLayer('briefing-annotations-line')) m.setLayoutProperty('briefing-annotations-line', 'visibility', 'visible');
            if (m.getLayer('briefing-annotations-label')) m.setLayoutProperty('briefing-annotations-label', 'visibility', 'visible');
          }
        }
      }
    } else if (hoverFeature.type === 'aquifer') {
      // Show aquifer boundary overlay (layer created at map load)
      if (m.getLayer('briefing-aquifer-fill')) m.setLayoutProperty('briefing-aquifer-fill', 'visibility', 'visible');
      if (m.getLayer('briefing-aquifer-outline')) m.setLayoutProperty('briefing-aquifer-outline', 'visibility', 'visible');
    } else if (hoverFeature.type === 'gas-supply') {
      for (const layerId of RADIUS_RING_LAYERS) {
        if (m.getLayer(layerId)) m.setLayoutProperty(layerId, 'visibility', padsVisibleRef.current ? 'none' : 'visible');
      }
    } else if (hoverFeature.type === 'oil-gas-wells') {
      // Site Intelligence "Surface" card: the wells, their laterals and the
      // gathering that serves them. Paint values are reset from DEFAULT_PAINT
      // on the next hover change like every other emphasis above.
      for (const id of ['briefing-oil-gas-wells', 'briefing-oil-gas-laterals', 'briefing-pipelines-gathering']) {
        if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', 'visible');
      }
      if (m.getLayer('briefing-oil-gas-wells')) {
        m.setPaintProperty('briefing-oil-gas-wells', 'circle-radius', 7);
        m.setPaintProperty('briefing-oil-gas-wells', 'circle-stroke-width', 2.5);
      }
      if (m.getLayer('briefing-oil-gas-laterals')) {
        m.setPaintProperty('briefing-oil-gas-laterals', 'line-width', 2.5);
        m.setPaintProperty('briefing-oil-gas-laterals', 'line-opacity', 1);
      }
      if (m.getLayer('briefing-pipelines-gathering')) {
        m.setPaintProperty('briefing-pipelines-gathering', 'line-width', 3);
        m.setPaintProperty('briefing-pipelines-gathering', 'line-opacity', 0.9);
      }
    } else if (hoverFeature.type === 'flood' || hoverFeature.type === 'wetlands') {
      // Polygon screens: a wash the imagery still reads through, outlines up.
      const group = LAYER_GROUP[hoverFeature.type === 'flood' ? 'briefing-flood-zones-fill' : 'briefing-wetlands-fill'] || [];
      for (const id of group) {
        if (!m.getLayer(id)) continue;
        m.setLayoutProperty(id, 'visibility', 'visible');
        if (id.endsWith('-fill')) m.setPaintProperty(id, 'fill-opacity', 0.45);
        else { m.setPaintProperty(id, 'line-width', 2); m.setPaintProperty(id, 'line-opacity', 1); }
      }
    } else if (hoverFeature.type === 'parcel') {
      if (m.getLayer('briefing-parcel-outline')) {
        m.setPaintProperty('briefing-parcel-outline', 'line-width', 5);
        m.setPaintProperty('briefing-parcel-outline', 'line-opacity', 1);
      }
      if (m.getLayer('briefing-carve-out-outline')) {
        m.setPaintProperty('briefing-carve-out-outline', 'line-width', 3.5);
        m.setPaintProperty('briefing-carve-out-outline', 'line-opacity', 1);
      }
    }
  }, [hoverFeature, mapLoaded, parcel, mapLayers, hoverConfig, isFullView, pipelines, effectiveSection, gasStyle]); // eslint-disable-line react-hooks/exhaustive-deps

  // The report hover owns emphasis while it is active. When it yields, restore
  // the locked layer selection from the control panel without baking generated
  // expressions into the next baseline.
  useEffect(() => {
    const m = mapRef.current;
    if (!mapLoaded || !m || hoverFeature || !layerEmphasis) return;

    const snapshot = new Map<string, PaintSnapshot>();
    emphasisSnapshotRef.current = snapshot;
    const remember = (id: string, properties: string[]) => {
      if (!m.getLayer(id) || snapshot.has(id)) return;
      const values: Record<string, unknown> = {};
      for (const property of properties) values[property] = clonePaintValue(m.getPaintProperty(id, property as any));
      snapshot.set(id, {
        visibility: String(m.getLayoutProperty(id, 'visibility') || 'visible'),
        properties: values,
      });
    };
    const setPaint = (id: string, property: string, highlighted: unknown, baseline: unknown, match: unknown[] | null) => {
      m.setPaintProperty(id, property as any, (match ? ['case', match, highlighted, baseline] : highlighted) as any);
    };

    for (const definition of LAYER_DEFINITIONS) {
      if (!layerEmphasis.memberIds.includes(definition.id) || definition.id === 'briefing-fiber-fill') continue;
      const targetApplies = layerEmphasis.target
        && (layerEmphasis.target.layer === definition.dataKey || layerEmphasis.target.layer === definition.id);
      const match = targetApplies
        ? ['in', ['to-string', ['get', layerEmphasis.target!.property]], ['literal', layerEmphasis.target!.values]]
        : null;
      const exact = Boolean(match);

      for (const id of definition.layerIds) {
        const layer = m.getLayer(id);
        if (!layer) continue;
        if (layer.type === 'line') {
          remember(id, ['line-opacity', 'line-width']);
          m.setLayoutProperty(id, 'visibility', 'visible');
          const baseOpacity = snapshot.get(id)!.properties['line-opacity'];
          const baseWidth = snapshot.get(id)!.properties['line-width'];
          if (id.includes('glow')) {
            setPaint(id, 'line-opacity', exact ? 0.6 : 0.45, baseOpacity, match);
            setPaint(id, 'line-width', id === 'briefing-pipelines-desert-sw-glow' ? 24 : 22, baseWidth, match);
          } else if (id.includes('casing') || id === 'briefing-pipelines-gas') {
            setPaint(id, 'line-opacity', 1, baseOpacity, match);
            setPaint(id, 'line-width', ['+', baseWidth as any, exact ? 3 : 1.5], baseWidth, match);
          } else {
            setPaint(id, 'line-opacity', 1, baseOpacity, match);
            setPaint(id, 'line-width', id.includes('outline') ? (exact ? 3 : 2) : (exact ? 8 : 5), baseWidth, match);
          }
        } else if (layer.type === 'fill') {
          remember(id, ['fill-opacity']);
          m.setLayoutProperty(id, 'visibility', 'visible');
          const baseOpacity = snapshot.get(id)!.properties['fill-opacity'];
          setPaint(id, 'fill-opacity', exact ? 0.6 : 0.4, baseOpacity, match);
        } else if (layer.type === 'circle') {
          remember(id, ['circle-opacity', 'circle-radius', 'circle-stroke-width']);
          m.setLayoutProperty(id, 'visibility', 'visible');
          const values = snapshot.get(id)!.properties;
          setPaint(id, 'circle-opacity', 1, values['circle-opacity'], match);
          setPaint(id, 'circle-radius', exact ? 16 : 12, values['circle-radius'], match);
          setPaint(id, 'circle-stroke-width', exact ? 4 : 3, values['circle-stroke-width'], match);
        } else if (layer.type === 'symbol') {
          remember(id, ['text-opacity']);
          m.setLayoutProperty(id, 'visibility', 'visible');
          const baseOpacity = snapshot.get(id)!.properties['text-opacity'];
          setPaint(id, 'text-opacity', 1, baseOpacity, match);
        }
      }
    }

    return () => {
      if (mapRef.current !== m) return;
      for (const [id, saved] of snapshot) {
        if (!m.getLayer(id)) continue;
        m.setLayoutProperty(id, 'visibility', saved.visibility as 'visible' | 'none');
        for (const [property, value] of Object.entries(saved.properties)) {
          if (value !== undefined) m.setPaintProperty(id, property as any, value as any);
        }
      }
      if (emphasisSnapshotRef.current === snapshot) emphasisSnapshotRef.current = new Map();
    };
  }, [
    briefingMode,
    carveOuts,
    effectiveSection,
    gasStyle,
    hoverFeature,
    isFullView,
    layerEmphasis,
    mapLayers,
    mapLoaded,
    radiusRingsFC,
    siteLayout,
  ]);

  // The footprint screen uses its own flood polygons and well buffers. Remove
  // the distance-ring wash so it cannot read as another exclusion boundary.
  useEffect(() => {
    const m = mapRef.current;
    if (!mapLoaded || !m || !footprintScreen) return;
    const saved = new Map<string, string>();
    for (const id of [...RADIUS_RING_LAYERS, 'briefing-flood-zones-fill', 'briefing-flood-zones-outline', 'briefing-flood-zones-twdb-outline']) {
      if (!m.getLayer(id)) continue;
      saved.set(id, String(m.getLayoutProperty(id, 'visibility') || 'visible'));
      m.setLayoutProperty(id, 'visibility', 'none');
    }
    for (const id of ['briefing-oil-gas-wells', 'briefing-pipelines-gathering']) {
      if (!m.getLayer(id)) continue;
      saved.set(id, String(m.getLayoutProperty(id, 'visibility') || 'visible'));
      m.setLayoutProperty(id, 'visibility', 'visible');
    }
    return () => {
      try {
        for (const [id, visibility] of saved) if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', visibility as 'visible' | 'none');
      } catch { /* The enclosing map may already have been removed. */ }
    };
  }, [footprintScreen, mapLoaded, mapLayers, hoverFeature, layerEmphasis, effectiveSection, isFullView, briefingMode]);

  // Consumer coverage polygons do not establish enterprise service, even while
  // the Fiber category or report section is selected.
  useEffect(() => {
    const m = mapRef.current;
    if (!mapLoaded || !m) return;
    for (const id of ['briefing-fiber-fill', 'briefing-fiber-outline']) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', 'none');
    }
  }, [briefingMode, effectiveSection, hoverFeature, isFullView, layerEmphasis, mapLoaded, mapLayers]);

  // Capture Reset's camera only when the actual view identity changes. In Full
  // View, ordinary report scrolling does not change layerViewKey and therefore
  // cannot move the home target.
  useEffect(() => {
    const m = mapRef.current;
    if (!mapLoaded || !m) return;
    let cancelled = false;
    const captureHome = () => {
      if (cancelled) return;
      homeCameraRef.current = {
        center: m.getCenter().toArray() as [number, number],
        zoom: m.getZoom(),
        bearing: m.getBearing(),
        pitch: m.getPitch(),
        padding: m.getPadding(),
      };
    };
    if (m.isMoving()) {
      const captureOnce = () => {
        m.off('moveend', captureOnce);
        captureHome();
      };
      m.on('moveend', captureOnce);
      return () => {
        cancelled = true;
        m.off('moveend', captureOnce);
      };
    }
    else captureHome();
    return () => {
      cancelled = true;
      m.off('moveend', captureHome);
    };
  }, [layerViewKey, mapLoaded]);

  const resetView = useCallback(() => {
    const m = mapRef.current;
    if (!m) return;
    disarmMeasurements();
    popupRef.current?.remove();
    popupRef.current = null;
    hoverPopupRef.current?.remove();
    hoverPopupRef.current = null;
    layerControlsRef.current?.resetTransientSelection();
    const home = homeCameraRef.current;
    if (home) {
      m.stop();
      m.jumpTo(home);
    }
  }, [disarmMeasurements]);
  const toggleMeasure = useCallback(() => {
    if (measureActive) measure.finish();
    armMeasure(!measureActive);
  }, [armMeasure, measure.finish, measureActive]);

  // Expose captureSnapshots for PDF export
  useImperativeHandle(ref, () => ({
    captureSnapshots: async () => {
      const m = mapRef.current;
      if (!m) throw new Error('Map not loaded');
      const center: [number, number] = [Number(parcel.lng), Number(parcel.lat)];
      const camera: MapCameraState = {
        center: m.getCenter().toArray() as [number, number],
        zoom: m.getZoom(),
        bearing: m.getBearing(),
        pitch: m.getPitch(),
        padding: m.getPadding(),
      };
      const transientLayers = [
        ...ALL_LAYERS,
        ...RADIUS_RING_LAYERS,
        'meiners-screen-fill', 'meiners-screen-line', 'meiners-candidate-label',
        'briefing-annotations-line',
        'briefing-annotations-label',
        'measurement-lines',
        'measurement-preview',
        'measurement-points',
        'measurement-area-fill',
        'measurement-area-line',
        'briefing-layer-location-link',
        'briefing-layer-location-site-fill',
        'briefing-layer-location-site-outline',
      ];
      const visibility = new Map<string, string>();
      const paint = new Map<string, Record<string, unknown>>();
      for (const layerId of transientLayers) {
        if (!m.getLayer(layerId)) continue;
        visibility.set(layerId, String(m.getLayoutProperty(layerId, 'visibility') || 'visible'));
        const styleLayer = m.getStyle().layers?.find(layer => layer.id === layerId) as { paint?: Record<string, unknown> } | undefined;
        if (styleLayer?.paint) paint.set(layerId, clonePaintValue(styleLayer.paint) as Record<string, unknown>);
      }
      const annotationSource = m.getSource('briefing-annotations') as (mapboxgl.GeoJSONSource & { serialize?: () => { data?: GeoJSON.GeoJSON | string } }) | undefined;
      const annotationData = annotationSource?.serialize?.().data;
      disarmMeasurements();

      const captureState = async (
        layers: string[],
        zoom: number,
        annotationType?: 'pipeline' | 'substation' | null,
      ): Promise<string> => {
        m.stop();
        // Set layer visibility
        // The core is force-shown only while the original layout is selected —
        // with an alternative active, the snapshot must show that alternative
        // (its layers sit outside ALL_LAYERS, so they keep the switcher's state).
        const originalLayoutActive = !mapLayers.siteAlternatives || siteLayoutRef.current === 'original';
        for (const layerId of ALL_LAYERS) {
          if (!m.getLayer(layerId)) continue;
          const visible = layers.includes(layerId)
            || (originalLayoutActive && (layerId === 'briefing-parcel-fill' || layerId === 'briefing-parcel-outline'));
          m.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
        }
        // Hide radius rings for all snapshots (they may be visible from scroll-sync)
        for (const layerId of RADIUS_RING_LAYERS) {
          if (m.getLayer(layerId)) m.setLayoutProperty(layerId, 'visibility', 'none');
        }
        for (const id of ['meiners-screen-fill', 'meiners-screen-line', 'meiners-candidate-label']) {
          if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', 'none');
        }
        // Annotations
        const annotationSource = m.getSource('briefing-annotations') as mapboxgl.GeoJSONSource | undefined;
        if (annotationSource) {
          if (annotationType === 'pipeline') {
            buildPipelineAnnotation(parcel, pipelines, annotationSource, mapLayers);
            if (m.getLayer('briefing-annotations-line')) m.setLayoutProperty('briefing-annotations-line', 'visibility', 'visible');
            if (m.getLayer('briefing-annotations-label')) m.setLayoutProperty('briefing-annotations-label', 'visibility', 'visible');
          } else if (annotationType === 'substation') {
            buildSubstationAnnotation(parcel, annotationSource, mapLayers);
            if (m.getLayer('briefing-annotations-line')) m.setLayoutProperty('briefing-annotations-line', 'visibility', 'visible');
            if (m.getLayer('briefing-annotations-label')) m.setLayoutProperty('briefing-annotations-label', 'visibility', 'visible');
          } else {
            annotationSource.setData(EMPTY_FC);
            if (m.getLayer('briefing-annotations-line')) m.setLayoutProperty('briefing-annotations-line', 'visibility', 'none');
            if (m.getLayer('briefing-annotations-label')) m.setLayoutProperty('briefing-annotations-label', 'visibility', 'none');
          }
        }
        // Jump (no animation for capture)
        m.jumpTo({ center, zoom });
        // Wait for map idle
        await new Promise<void>(resolve => m.once('idle', () => resolve()));
        return m.getCanvas().toDataURL('image/png');
      };

      try {
        for (const layerId of transientLayers) {
          if ((layerId.startsWith('measurement-') || layerId.startsWith('briefing-layer-location-')) && m.getLayer(layerId)) {
            m.setLayoutProperty(layerId, 'visibility', 'none');
          }
        }
        for (const [layerId, defaults] of Object.entries(DEFAULT_PAINT)) {
          if (!m.getLayer(layerId)) continue;
          for (const [property, value] of Object.entries(defaults)) {
            m.setPaintProperty(layerId, property as any, value as any);
          }
        }
        applyGasWidths(m);
        const cover = await captureState([], 12);
        const fuelDelivery = await captureState(
          ['briefing-pipelines-desert-sw', 'briefing-pipelines-desert-sw-glow', 'briefing-pipelines-gas-glow', 'briefing-pipelines-gas-casing', 'briefing-pipelines-gas', 'briefing-pipelines-gas-label', 'briefing-pipelines-gathering'],
          12, 'pipeline'
        );
        const infrastructure = await captureState(['briefing-substations'], 11, 'substation');
        return { cover, fuelDelivery, infrastructure };
      } finally {
        for (const [layerId, value] of visibility) {
          if (m.getLayer(layerId)) m.setLayoutProperty(layerId, 'visibility', value as 'visible' | 'none');
        }
        for (const [layerId, properties] of paint) {
          if (!m.getLayer(layerId)) continue;
          for (const [property, value] of Object.entries(properties)) m.setPaintProperty(layerId, property as any, value as any);
        }
        if (annotationSource && annotationData != null) annotationSource.setData(annotationData as any);
        m.stop();
        m.jumpTo(camera);
      }
    },
  }), [applyGasWidths, disarmMeasurements, mapLoaded, mapLayers, parcel, pipelines]);

  const toolHost = mapLoaded
    ? mapRef.current?.getContainer().querySelector('.mapboxgl-ctrl-top-right')
    : null;

  return (
    <div className={className} style={{ width: '45%', position: 'relative', ...style }}>
      <style>{`
        .briefing-pipeline-popup .mapboxgl-popup-content {
          background: #F7F5F2;
          border-radius: 6px;
          padding: 10px 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.25);
        }
        .briefing-pipeline-popup .mapboxgl-popup-tip {
          border-top-color: #F7F5F2;
          border-bottom-color: #F7F5F2;
          border-left-color: #F7F5F2;
          border-right-color: #F7F5F2;
        }
        .briefing-pipeline-popup .mapboxgl-popup-close-button {
          color: #6A7E8E;
          font-size: 16px;
          padding: 2px 6px;
        }
      `}</style>
      <div ref={mapContainerRef} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} />

      {toolHost && createPortal(
        <div className="mapboxgl-ctrl mapboxgl-ctrl-group briefing-map-tool-rail" aria-label="Map tools">
          <button type="button" onClick={resetView} title="Reset map view" aria-label="Reset map view">
            <RotateCcw size={15} aria-hidden="true" />
          </button>
          <button
            type="button"
            className={measureActive ? 'is-active' : undefined}
            onClick={toggleMeasure}
            title="Measure distance — click to add points; double-click or Escape to finish"
            aria-label="Measure distance"
            aria-pressed={measureActive}
          >
            <Ruler size={15} aria-hidden="true" />
          </button>
          <button
            type="button"
            className={areaActive ? 'is-active' : undefined}
            onClick={() => armArea(!areaActive)}
            title="Measure acreage — drag a rectangle; Escape clears"
            aria-label="Measure acreage"
            aria-pressed={areaActive}
          >
            <SquareDashed size={15} aria-hidden="true" />
          </button>
          {measure.count > 0 && (
            <button type="button" className="briefing-map-tool-clear" onClick={measure.clearAll} title="Clear distance measurements" aria-label={`Clear ${measure.count} distance measurements`}>
              ×<small>{measure.count}</small>
            </button>
          )}
        </div>,
        toolHost,
      )}

      {/* Top-left column: active-layers badge, with the western-band toggle
          (A1 only) directly beneath it, left-aligned to the badge edge. */}
      <div style={{ position: 'absolute', top: 10, left: 10, width: 'calc(100% - 70px)', maxWidth: 450, zIndex: 5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
      <BriefingLayerControls
        ref={layerControlsRef}
        map={mapLoaded ? mapRef.current : null}
        data={layerData}
        footprint={displayedFootprint}
        availableMemberIds={availableMemberIds}
        viewKey={layerViewKey}
        measurementActive={measureActive || areaActive}
        onEmphasisChange={setLayerEmphasis}
        onBeforeLocation={disarmMeasurements}
      />

      {pendingFemaLabels(mapLayers.floodZones).map(label => (
        <div key={label} style={{ background: 'rgba(46,56,64,0.92)', color: PENDING_FEMA_COLOR, padding: '6px 10px', borderRadius: 4, fontSize: 10 }}>{label}</div>
      ))}
      {mapLayers.floodZones.truncated && <div role="status" style={{ background: 'rgba(46,56,64,0.92)', color: '#E4E6E8', padding: '6px 10px', borderRadius: 4, fontSize: 10 }}>Flood overlay is incomplete at this extent.</div>}

      {meinersFootprint && <MeinersFootprintControls map={mapLoaded ? mapRef.current : null} layerRevision={mapLayers} footprint={meinersFootprint} />}

      {/* Site-layout switcher (A1 only — alternatives served only for that
          carve-out). Outlines only: nothing on the page is rescored by a switch. */}
      {mapLayers.siteAlternatives && (
        <div className="briefing-controls" style={{
          background: 'rgba(46,56,64,0.88)', color: '#E4E6E8',
          padding: '6px 10px', borderRadius: 4, fontSize: 9, maxWidth: 260,
        }}>
          <div style={{ fontWeight: 700, letterSpacing: 0.5, marginBottom: 4 }}>SITE LAYOUT</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {[
              { key: 'original', label: 'ORIGINAL', title: 'Original premises — analyzed core + western band' },
              ...siteAlternativeFeatures.map(f => {
                const key = String(f.properties?.key || '');
                const digits = /^alt(\d+)$/i.exec(key);
                return {
                  key,
                  label: digits ? `ALT ${digits[1]}` : String(f.properties?.name || key).toUpperCase(),
                  title: String(f.properties?.source || ''),
                };
              }),
            ].map(chip => (
              <span
                key={chip.key}
                onClick={() => changeSiteLayout(chip.key)}
                title={chip.title}
                style={{
                  background: siteLayout === chip.key ? 'rgba(80,98,114,0.95)' : 'transparent',
                  border: siteLayout === chip.key ? '1px solid #C4705A' : '1px solid transparent',
                  color: '#E4E6E8', padding: '3px 8px', borderRadius: 3,
                  fontWeight: 600, letterSpacing: 0.5, cursor: 'pointer', userSelect: 'none',
                }}
              >
                {chip.label}
              </span>
            ))}
          </div>
          {activeAlternative && (
            <>
              <div style={{ marginTop: 5, fontSize: 8, color: '#E4E6E8', lineHeight: 1.5 }}>
                {activeAlternative.properties?.measured_acres != null
                  ? `${Number(activeAlternative.properties.acres).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ac · Crown Pine tract acreage`
                  : `${Math.round(Number(activeAlternative.properties?.acres) || 0).toLocaleString()} ac measured`}
                {` · +${Math.round(Number(activeAlternative.properties?.added_ac) || 0).toLocaleString()} ac added`}
                {` / −${Math.round(Number(activeAlternative.properties?.dropped_ac) || 0).toLocaleString()} ac dropped vs original`}
                {` · gas line crossing ~${(Number(activeAlternative.properties?.texoma_crossing_mi) || 0).toFixed(2)} mi`}
              </div>
              <div style={{ marginTop: 3, fontSize: 8, color: '#8A9CAA', fontStyle: 'italic', lineHeight: 1.5 }}>
                {activeAlternative.properties?.measured_acres != null
                  ? `Mapped calculations use the ${Number(activeAlternative.properties.measured_acres).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}-acre fitted polygon; pending title examination and any elected survey.`
                  : 'Grades, slope, flood and water on this page are computed on the original analyzed core.'}
              </div>
            </>
          )}
        </div>
      )}
      {/* Ghost-layer chips (A1 Alt only), both default OFF: ACREAGE BOXES = the clean
          rectangles for the layout on screen; CHANGES = the added/dropped pieces
          of the selected alternative (alt only). */}
      {mapLayers.sitePads && illustrativeAcreage && (
        <div style={{ maxWidth: 250, fontSize: 9, color: '#B8C3CC', lineHeight: 1.4 }}>
          Acreage boxes illustrate the scale of areas across the assembly.
        </div>
      )}
      {mapLayers.sitePads && (
        <div
          onClick={() => {
            const m = mapRef.current;
            const next = !padsVisible;
            setPadsVisible(next);
            if (m) {
              for (const id of SITE_PAD_LAYERS) if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', next ? 'visible' : 'none');
              // Radius rings (the 2 / 5 / 10-mi wash) go off with the pads on, and
              // come back only where the view normally shows them (Full View).
              const ringsBack = !next && (isFullView || briefingMode === 'checklist');
              for (const id of RADIUS_RING_LAYERS) if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', ringsBack ? 'visible' : 'none');
            }
          }}
          style={{
            background: padsVisible ? 'rgba(80,98,114,0.95)' : 'rgba(46,56,64,0.88)',
            color: '#E4E6E8', padding: '5px 10px', borderRadius: 4, fontSize: 9,
            fontWeight: 600, letterSpacing: 0.5, cursor: 'pointer', userSelect: 'none',
            border: padsVisible ? '1px solid #C4705A' : '1px solid transparent',
            display: 'flex', alignItems: 'center', gap: 5,
          }}
          title={illustrativeAcreage ? 'Acreage boxes illustrate the scale of areas across the assembly. Existing shapes and acreage labels are retained for orientation. Hover for size, reference terrain and distance to the gas line.' : 'Acreage boxes for the layout on screen — contiguous acreage, not graded pads. No box crosses County Road 358 / Durham Ranch Road, the public county road through the site. Original / Alt 2: clean rectangles that also avoid streams, wetlands and the 100-yr floodplain. Alt 1 / Alt 3: engineered basis — avoids the floodplain, forested wetland and Shawnee Creek; crosses intermittent channels (footage in the hover). Hover for size, slope and distance to the gas line'}
        >
          <span style={{ width: 8, height: 8, borderRadius: 1, border: '1.5px dashed #e8b878', flexShrink: 0 }} />
          ACREAGE BOXES {padsVisible ? 'ON' : 'OFF'}
        </div>
      )}
      {mapLayers.siteAlternativePieces && activeAlternative && (
        <div
          onClick={() => setPiecesVisible(v => !v)}
          style={{
            background: piecesVisible ? 'rgba(80,98,114,0.95)' : 'rgba(46,56,64,0.88)',
            color: '#E4E6E8', padding: '5px 10px', borderRadius: 4, fontSize: 9,
            fontWeight: 600, letterSpacing: 0.5, cursor: 'pointer', userSelect: 'none',
            border: piecesVisible ? '1px solid #C4705A' : '1px solid transparent',
            display: 'flex', alignItems: 'center', gap: 5,
          }}
          title="Where this alternative differs from the original: added and dropped pieces, each with acres, upland and under-5% slope"
        >
          <span style={{ width: 8, height: 8, borderRadius: 2, background: 'rgba(244,236,216,0.35)', border: '1px dashed #E4E6E8', flexShrink: 0 }} />
          CHANGES {piecesVisible ? 'ON' : 'OFF'}
        </div>
      )}

      {/* Western-band toggle (A1 only — layer served only for that carve-out).
          Hidden while an alternative layout is on screen: the band belongs to
          the original premises, which that layout replaces. */}
      {mapLayers.siteBand && siteLayout === 'original' && (
        <div
          onClick={() => {
            const m = mapRef.current;
            const next = !accessTractVisible;
            setAccessTractVisible(next);
            if (m) {
              for (const id of SITE_BAND_LAYERS) {
                if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', next ? 'visible' : 'none');
              }
            }
          }}
          style={{
            background: accessTractVisible ? 'rgba(80,98,114,0.95)' : 'rgba(46,56,64,0.88)',
            color: '#E4E6E8', padding: '5px 10px', borderRadius: 4, fontSize: 9,
            fontWeight: 600, letterSpacing: 0.5, cursor: 'pointer', userSelect: 'none',
            border: accessTractVisible ? '1px solid #C4705A' : '1px solid transparent',
            display: 'flex', alignItems: 'center', gap: 5,
          }}
          title="Toggle the site's western band (±594 ac; the ET Texoma line crosses it)"
        >
          <span style={{ width: 8, height: 8, borderRadius: 2, background: 'rgba(255,255,255,0.35)', border: '1px solid #E4E6E8', flexShrink: 0 }} />
          WESTERN BAND {accessTractVisible ? 'ON' : 'OFF'}
        </div>
      )}

      </div>

      {/* Section indicator */}
      <div style={{
        position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)',
        background: 'rgba(46,56,64,0.88)', color: '#E4E6E8',
        padding: '5px 12px', borderRadius: 4, fontSize: 10, whiteSpace: 'nowrap',
        pointerEvents: 'none',
      }}>
        Viewing: <span style={{ color: '#C4705A', fontWeight: 500 }}>{briefingMode === 'checklist' ? 'DD Checklist' : isFullView ? 'Full View' : SECTION_LABELS[activeSection]}</span>
      </div>

      {/* Print + Share buttons */}
      <div className="briefing-controls" style={{
        position: 'absolute', bottom: 12, right: 12,
        display: 'flex', gap: 8,
      }}>
        {onPrint && (
          <button
            onClick={onPrint}
            title="For cleanest output: uncheck 'Headers and footers' in the print dialog"
            style={{
              background: 'rgba(46,56,64,0.92)', color: '#E4E6E8', border: '1px solid rgba(80,98,114,0.20)',
              padding: '8px 16px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
              fontWeight: 600, letterSpacing: '0.3px',
            }}
          >
            Print PDF
          </button>
        )}
        {onShare && (
          <button
            onClick={onShare}
            style={{
              background: '#2E3840', color: '#D49880', border: 'none',
              padding: '8px 16px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
              fontWeight: 600, letterSpacing: '0.3px',
            }}
          >
            Share Link
          </button>
        )}
      </div>
    </div>
  );
});

// --- Annotation helpers ---

function buildPipelineAnnotation(
  parcel: ParcelDetail,
  pipelines: NearbyPipeline[],
  source: mapboxgl.GeoJSONSource,
  mapLayers: BriefingMapLayers,
) {
  const center: [number, number] = [Number(parcel.lng), Number(parcel.lat)];

  // Find the nearest point on pipeline geometry using the raw GeoJSON data
  // (not querySourceFeatures, which returns tile-clipped/simplified geometry).
  // Project onto every line segment to find the true closest point.
  let closestPoint: [number, number] | null = null;
  let closestDist = Infinity;

  // Prefer Desert SW, fall back to gas transmission
  for (const fc of [mapLayers.pipelinesDesertSw, mapLayers.pipelinesGas]) {
    if (!fc?.features?.length) continue;
    for (const f of fc.features) {
      const pt = nearestPointOnGeometry(f.geometry, center);
      if (pt) {
        const d = Math.hypot(pt[0] - center[0], pt[1] - center[1]);
        if (d < closestDist) {
          closestDist = d;
          closestPoint = pt;
        }
      }
    }
    if (closestPoint && fc === mapLayers.pipelinesDesertSw) break;
  }

  if (!closestPoint) {
    source.setData(EMPTY_FC);
    return;
  }

  // Find distance label — prefer Desert SW pipeline from the data
  const desertPipeline = pipelines.find(p => isDesertSW(p.name));
  const nearest = desertPipeline || (pipelines.length > 0
    ? pipelines.reduce((a, b) => Number(a.distance_miles) < Number(b.distance_miles) ? a : b)
    : null);
  const pipelineName = desertPipeline ? 'Desert Southwest' : (nearest?.name?.replace(/\s+/g, ' ').trim() || '');
  const label = nearest ? `${pipelineName ? pipelineName + ' — ' : ''}${Number(nearest.distance_miles).toFixed(1)} mi` : '';

  const midpoint: [number, number] = [
    (center[0] + closestPoint[0]) / 2,
    (center[1] + closestPoint[1]) / 2,
  ];

  const fc: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [center, closestPoint] },
        properties: {},
      },
      {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: midpoint },
        properties: { label },
      },
    ],
  };

  source.setData(fc);
}

const esc = (s: unknown) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export function buildPipelinePopupHTML(
  featureProps: Partial<NearbyPipeline>,
): string {
  // Map features are individual pipeline UUIDs. Grouped system rows combine
  // member values, so even their representative ID cannot safely supplement a
  // selected segment's capacity, diameter, pressure, utilization, or distance.
  const selected = featureProps;
  const name = selected.name || 'Unknown Pipeline';
  const operator = selected.operator;
  const diameter = selected.diameter_inches;
  const maop = selected.maop_psig;
  const maopSource = selected.maop_source;
  const pressureGrade = selected.pressure_grade;
  const systemType = selected.system_type;
  const capacity = pipelineCapacityDisplay(selected);
  const cappedCapacity = capacity.capacityMmcf;
  const utilization = selected.utilization_pct;
  const distance = selected.distance_miles;
  const isMeiners = selected.distance_basis === 'carve_out_boundary';
  const meinersCapacity = isMeiners ? meinersPipelineCapacity(selected) : null;

  const featured = selected.headline_override || selected.co_headline;

  const lines: string[] = [];
  lines.push(`<div style="font-size:13px;font-weight:700;color:#C4705A;margin-bottom:4px">${esc(name)}</div>`);
  if (featured) {
    lines.push(`<div style="display:inline-block;font-size:9px;font-weight:600;letter-spacing:0.05em;text-transform:uppercase;background:#C4705A;color:#fff;padding:1px 6px;border-radius:2px;margin-bottom:4px">Featured tap candidate</div>`);
  }
  if (operator || diameter) {
    const parts: string[] = [];
    if (operator) parts.push(esc(operator));
    if (diameter) parts.push(`${esc(diameter)}" diameter`);
    lines.push(`<div style="margin-bottom:2px">${parts.join(' · ')}</div>`);
  }
  if (maop) {
    const parts: string[] = [`MAOP: ${Number(maop).toLocaleString()} psig`];
    if (pressureGrade) parts[0] += ` (${esc(pressureGrade)})`;
    if (maopSource) parts.push(esc(maopSource));
    if (systemType) parts.push(esc(systemType.charAt(0).toUpperCase() + systemType.slice(1)));
    lines.push(`<div style="margin-bottom:2px">${parts.join(' · ')}</div>`);
  }
  if (meinersCapacity) {
    lines.push(`<div style="margin-bottom:2px">${esc(meinersCapacity.label)}: ${esc(meinersCapacity.value)}</div>`);
    lines.push(`<div style="color:#6A7E8E">${esc(meinersCapacity.detail)}</div>`);
    if (meinersCapacity.sourceUrl && meinersCapacity.sourceTitle) {
      lines.push(`<div style="color:#6A7E8E">Source: <a href="${esc(meinersCapacity.sourceUrl)}" target="_blank" rel="noopener noreferrer">${esc(meinersCapacity.sourceTitle)}</a></div>`);
    }
  } else if (cappedCapacity != null) {
    let text = `${esc(capacity.label)}: ${Math.round(cappedCapacity).toLocaleString()} MMcf/d`;
    if (utilization != null) text += ` · ${utilization}% utilized`;
    lines.push(`<div style="margin-bottom:2px">${text}</div>`);
  } else {
    lines.push(`<div style="margin-bottom:2px">${esc(capacity.label)}</div>`);
    lines.push(`<div style="color:#6A7E8E">${esc(capacity.explanation)}</div>`);
  }
  if (distance != null) {
    lines.push(`<div style="color:#6A7E8E">${distance} mi from ${selected.distance_basis === 'carve_out_boundary' ? 'full block boundary' : 'parcel'}</div>`);
  }

  return `<div style="font-size:11px;color:#2E3840;line-height:1.5">${lines.join('')}</div>`;
}

function haversineMi(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3959;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function buildFeaturePopupHTML(layerId: string, props: Record<string, unknown>, distMi: number): string {
  const name = esc(props.name);
  const operator = props.operator ? esc(props.operator) : '';

  let title = '';
  let lines: string[] = [];

  switch (layerId) {
    case 'briefing-substations':
      title = name || 'Substation';
      lines = [operator, props.voltage_kv ? `${props.voltage_kv} kV` : ''].filter(Boolean);
      break;
    case 'briefing-osm-switchyards':
      title = name || 'Switchyard';
      lines = [operator, props.voltage_kv ? `${props.voltage_kv} kV` : ''].filter(Boolean);
      break;
    case 'briefing-gas-plants': {
      title = name || 'Gas Processing Plant';
      const gpParts = [operator];
      if (props.capacity_mmcf_d) gpParts.push(`${Number(props.capacity_mmcf_d).toLocaleString()} MMcf/d`);
      if (props.status && props.status !== 'operational') gpParts.push(esc(props.status));
      lines = gpParts.filter(Boolean);
      break;
    }
    case 'briefing-oil-gas-wells': {
      title = name || 'Well';
      const statusLabel: Record<string, string> = { producing: 'Producing', plugged: 'Plugged', permitted: 'Permitted location', dry_hole: 'Dry hole', shut_in: 'Shut-in', injection: 'Injection / disposal', other: 'Well' };
      const wParts = [operator, `${statusLabel[String(props.status)] || esc(props.symbol || '')}${props.inside === true || props.inside === 'true' ? ' · on the tract' : ''}`];
      if (props.field) wParts.push(esc(props.field));
      if (props.lateral_length_ft) wParts.push(`Horizontal · ${Number(props.lateral_length_ft).toLocaleString()} ft lateral`);
      else if (props.wellbore_profile) wParts.push(esc(props.wellbore_profile));
      if (props.permit_approved) wParts.push(`Permit approved ${esc(String(props.permit_approved).slice(0, 4))}`);
      if (props.api_depth_ft) wParts.push(`${Number(props.api_depth_ft).toLocaleString()} ft TD`);
      wParts.push(`API ${esc(props.id)}`);
      lines = wParts.filter(Boolean);
      break;
    }
    case 'briefing-data-centers': {
      title = name || 'Data Center';
      const dcParts = [operator];
      if (props.capacity_mw) dcParts.push(`${Number(props.capacity_mw).toLocaleString()} MW`);
      if (props.status && props.status !== 'operational') dcParts.push(esc(props.status));
      lines = dcParts.filter(Boolean);
      const loc = [props.city, props.county].filter(Boolean).map(s => esc(s)).join(', ');
      if (loc) lines.push(loc);
      break;
    }
    case 'briefing-carrier-fiber':
      title = name || 'Carrier Fiber';
      break;
    case 'briefing-fiber-fill': {
      let provs: any[] = [];
      try { provs = Array.isArray(props.providers) ? props.providers : JSON.parse(String(props.providers)); } catch { /* Mapbox stringification */ }
      const count = Number(props.provider_count) || provs.length;
      title = `${count} Provider${count !== 1 ? 's' : ''}`;
      const dl = Number(props.max_download_mbps);
      const ul = Number(props.max_upload_mbps);
      if (dl) lines.push(`${dl >= 1000 ? (dl / 1000).toFixed(0) + ' Gbps' : dl + ' Mbps'} down`);
      if (ul) lines.push(`${ul >= 1000 ? (ul / 1000).toFixed(0) + ' Gbps' : ul + ' Mbps'} up`);
      // Tightened enterprise definition: only brc='B' (business-class) counts. brc='X'
      // (both-types) indicates the same provider also serves residential, which doesn't
      // guarantee dedicated/SLA-backed service.
      const hasEnt = provs.some((p: any) => p.brc === 'B');
      lines.push(hasEnt ? 'Enterprise' : 'Residential');
      if (provs.length) {
        // Lead with fastest business-class provider; fall back to fastest overall.
        const bySpeed = (a: any, b: any) => Number(b.download || 0) - Number(a.download || 0);
        const enterprise = provs.filter((p: any) => p.brc === 'B').sort(bySpeed);
        const ordered = enterprise.length ? [...enterprise, ...provs.filter((p: any) => p.brc !== 'B').sort(bySpeed)] : provs.slice().sort(bySpeed);
        const names = ordered.slice(0, 3).map((p: any) => esc(p.name || '')).filter(Boolean);
        if (names.length) lines.push(names.join(', '));
      }
      break;
    }
    case 'briefing-railroads': {
      title = operator || name || 'Railroad';
      if (operator && name && name !== operator) lines.push(name);
      const rrTypes: Record<string, string> = { M: 'Mainline', O: 'Branch', Y: 'Yard', I: 'Industrial', S: 'Siding', A: 'Abandoned', X: 'Out of Service', R: 'Restricted' };
      const rrLabel = rrTypes[String(props.network_type)] || '';
      if (rrLabel) lines.push(rrLabel);
      break;
    }
    case 'briefing-water-wells': {
      const use = name || 'Water Well';
      title = use.charAt(0).toUpperCase() + use.slice(1).toLowerCase();
      const wwParts: string[] = [];
      if (props.total_depth_ft) wwParts.push(`${Number(props.total_depth_ft).toLocaleString()} ft deep`);
      if (props.yield_gpm) wwParts.push(`${Number(props.yield_gpm).toLocaleString()} gpm`);
      lines = wwParts;
      break;
    }
    case 'briefing-wetlands-fill':
      title = name || 'Wetland';
      if (props.acres) lines.push(`${Number(props.acres).toFixed(1)} acres`);
      break;
    case 'briefing-airports': {
      const arptId = typeof props.arpt_id === 'string' ? props.arpt_id : null;
      const icaoId = typeof props.icao_id === 'string' ? props.icao_id : null;
      const far139 = typeof props.far_139_type === 'string' && props.far_139_type ? `FAR-139 ${props.far_139_type}` : null;
      const apClass = props.airport_class === 'commercial' ? 'Commercial' : props.airport_class === 'general_aviation' ? 'General aviation' : null;
      const maxPaved = props.max_paved_runway_ft != null ? Number(props.max_paved_runway_ft) : null;
      const runwaysRaw = props.runways;
      const runways: Array<{ id?: string|null; len_ft?: number; width_ft?: number|null; surface?: string|null; lit?: boolean }> = Array.isArray(runwaysRaw)
        ? runwaysRaw : (typeof runwaysRaw === 'string' ? JSON.parse(runwaysRaw) : []);
      const paved = runways.filter(r => r.surface && /^(ASPH|CONC)/i.test(r.surface));
      const longestPaved = paved.length ? paved.reduce((a, b) => Number(b.len_ft || 0) > Number(a.len_ft || 0) ? b : a, paved[0]) : null;
      title = (name || 'Airport') + (icaoId ? ` (${icaoId})` : arptId ? ` (${arptId})` : '');
      if (apClass) lines.push(apClass);
      if (far139) lines.push(far139);
      if (longestPaved) {
        const w = longestPaved.width_ft ? ` × ${longestPaved.width_ft} ft` : '';
        lines.push(`Longest paved ${Number(longestPaved.len_ft || 0).toLocaleString()} ft${w}`);
      } else if (maxPaved) {
        lines.push(`Longest paved ${maxPaved.toLocaleString()} ft`);
      }
      if (runways.length > 0) lines.push(`${runways.length} runway${runways.length === 1 ? '' : 's'}`);
      break;
    }
    case 'briefing-highways':
      title = name || 'Highway';
      break;
    case 'briefing-access-route-casing':
    case 'briefing-access-route': {
      title = name || 'Haul route';
      {
        // Three lines: can the load get there, how busy the road is, what stops it.
        const adt = props.station_latest_adt ?? props.aadt; const adtYear = props.station_latest_adt != null ? props.station_latest_year : props.aadt_year;
        const posted = Number(props.structures_posted) || 0;
        lines = [
          [props.miles != null ? `${Number(props.miles).toFixed(2)} road mi` : '', props.lanes ? `${props.lanes} lanes` : '', props.surface_width_ft ? `${props.surface_width_ft} ft wide` : '', roadSurfaceShort(String(props.surface || '')) || ''].filter(Boolean).join(' · '),
          adt != null ? [`Average daily traffic ${Number(adt).toLocaleString()}${adtYear ? ` (${esc(adtYear)})` : ''}`, truckShareWords(Number(props.truck_pct)) || '', trafficWord(Number(adt)) || ''].filter(Boolean).join(' · ') : '',
          [props.structures_on_route != null ? (posted ? `${posted} weight-limited structure${posted > 1 ? 's' : ''} on the route` : 'No weight-limited bridge or culvert') : '', props.clearance_under_ft ? `${feetInches(Number(props.clearance_under_ft))} clearance under ${esc(props.interchange || 'the interstate')} at the interchange` : ''].filter(Boolean).join(' · '),
        ].filter(Boolean);
      }
      break;
    }
    case 'briefing-access-points': {
      title = name || 'Structure';
      if (props.kind === 'structure') {
        lines = [
          `${esc(props.type || 'structure')}${props.year_built ? ` · built ${esc(props.year_built)}` : ''}`,
          props.posting_label === 'not posted' ? 'No weight limit' : esc(props.posting_label || ''),
          props.min_vert_clear_under_ft ? `${props.min_vert_clear_under_ft} ft clearance under` : '',
          props.roadway_width_ft ? `${props.roadway_width_ft} ft roadway` : '',
          props.condition_class ? `Condition ${esc(props.condition_class)}` : '',
          props.id ? `NBI ${esc(props.id)}` : '',
        ].filter(Boolean);
      } else if (props.kind === 'gate') {
        lines = [esc(props.basis || ''), props.section ? `Section ${esc(props.section)}` : ''].filter(Boolean);
      } else if (props.kind === 'station') {
        title = name || 'TxDOT count station';
        lines = [
          props.on_road ? `On ${esc(props.on_road)}` : '',
          props.latest_adt != null ? `Average daily traffic ${Number(props.latest_adt).toLocaleString()} (${esc(props.latest_year)} count)` : '',
          props.peak_adt != null ? `Peak ${Number(props.peak_adt).toLocaleString()} a day in ${esc(props.peak_year)}${props.first_adt != null ? ` · ${Number(props.first_adt).toLocaleString()} in ${esc(props.first_year)}` : ''}` : '',
        ].filter(Boolean);
      } else {
        lines = [
          props.via ? `${esc(props.via)} off ${esc(props.route || '')}` : '',
          props.network_mi_from_interchange != null ? `${props.network_mi_from_interchange} road mi from the interchange` : (props.straight_mi_from_interchange != null ? `${props.straight_mi_from_interchange} mi straight-line from the interchange` : ''),
        ].filter(Boolean);
      }
      break;
    }
    case 'briefing-public-roads':
      title = name || (props.system ? esc(String(props.system)) : 'Road');
      lines = [
        [props.system ? esc(props.system) : '', props.maintainer ? `${esc(props.maintainer)}-maintained` : ''].filter(Boolean).join(' · '),
        [props.lanes ? `${props.lanes} lanes` : '', props.surface_width_ft ? `${props.surface_width_ft} ft wide` : '', roadSurfaceShort(roadSurfaceFromCode(Number(props.surface_code))) || ''].filter(Boolean).join(' · '),
        props.aadt ? [`Average daily traffic ${Number(props.aadt).toLocaleString()}${props.aadt_year ? ` (${esc(props.aadt_year)})` : ''}`, truckShareWords(Number(props.truck_pct)) || '', trafficWord(Number(props.aadt)) || ''].filter(Boolean).join(' · ') : '',
      ].filter(Boolean);
      break;
    case 'briefing-frontage-roads':
      title = name || 'Frontage road';
      lines = [
        [props.system ? esc(props.system) : '', props.maintainer ? `${esc(props.maintainer)}-maintained` : '', props.frontage_mi != null ? `${props.frontage_mi} mi of frontage${props.side ? ` on the ${esc(props.side)} line` : ''}` : ''].filter(Boolean).join(' · '),
        [props.lanes ? `${props.lanes} lanes` : '', props.surface_width_ft ? `${props.surface_width_ft} ft wide` : '', roadSurfaceShort(String(props.surface || '')) || ''].filter(Boolean).join(' · '),
        props.aadt ? [`Average daily traffic ${Number(props.aadt).toLocaleString()}${props.aadt_year ? ` (${esc(props.aadt_year)})` : ''}`, truckShareWords(Number(props.truck_pct)) || '', trafficWord(Number(props.aadt)) || ''].filter(Boolean).join(' · ') : '',
      ].filter(Boolean);
      break;
    case 'briefing-frontage-tracks':
      title = name || 'Ranch road';
      lines = [
        props.inside_mi != null ? `${props.inside_mi} mi inside the fence` : '',
        props.tie_in ? `Ties into ${esc(props.tie_in)}` : 'No public-road tie-in mapped',
        [props.surface ? esc(props.surface) : '', props.access ? `access: ${esc(props.access)}` : ''].filter(Boolean).join(' · '),
        'OpenStreetMap feature — not a title record',
      ].filter(Boolean);
      break;
    case 'briefing-flood-zones-fill': {
      const twdbFlood = props.source === 'twdb_screening';
      const pending = props.source === 'fema_pending';
      title = pending ? `Pending FEMA Zone ${esc(props.zone_code)}` : twdbFlood ? 'TWDB 1% Annual-Chance Screen' : `FEMA Zone ${props.zone_code ? esc(props.zone_code) : '—'}`;
      lines = pending ? [esc(pendingFemaLabel(String(props.effective_date || ''))), props.flood_type === '500yr' ? '0.2% annual-chance hazard' : '1% annual-chance hazard', 'Future mapping'] : twdbFlood
        ? ['Non-regulatory screening layer', '2024 TWDB State Flood Plan · existing conditions']
        : ['FEMA 100-yr floodplain'];
      break;
    }
    default:
      title = name || 'Feature';
  }

  let html = `<div style="font-size:12px;color:#2E3840;line-height:1.5">`;
  html += `<div style="font-size:13px;font-weight:600;margin-bottom:1px">${title}</div>`;
  if (lines.length) html += `<div style="color:#6A7E8E">${lines.join(' · ')}</div>`;
  html += `<div style="color:#8A9CAA;font-size:10px;margin-top:3px">${distMi.toFixed(1)} mi from site</div>`;
  html += `</div>`;

  return html;
}

// Aggregate card for the Gas Transmission legend chip: one row per non-gathering
// system — name (+ featured chip), operator, diameter, and distance.
export function buildGasSystemsSummaryHTML(
  pipelines: NearbyPipeline[],
  options: { isMeiners?: boolean } = {},
): string | null {
  const systems = pipelines.filter(p => !p.is_gathering);
  if (!systems.length) return null;
  const rows = systems.map(p => {
    const featured = p.headline_override || p.co_headline;
    const meinersCapacity = options.isMeiners ? meinersPipelineCapacity(p) : null;
    const parts: string[] = [];
    if (p.operator) parts.push(esc(p.operator));
    if (p.diameter_inches) parts.push(`${esc(p.diameter_inches)}"`);
    if (p.distance_miles != null) parts.push(`${p.distance_miles} mi`);
    if (meinersCapacity) parts.push(`${meinersCapacity.label}: ${meinersCapacity.value}`);
    return `<div style="padding:5px 0;border-top:1px solid rgba(80,98,114,0.15)">
      <div style="font-weight:700;color:#C4705A;font-size:12px">${esc(p.name || p.operator || 'Pipeline')}${featured ? '<span style="font-size:8px;font-weight:600;letter-spacing:0.05em;text-transform:uppercase;background:#C4705A;color:#fff;padding:1px 5px;border-radius:2px;margin-left:6px;vertical-align:middle">Featured</span>' : ''}</div>
      <div style="color:#6A7E8E;font-size:10.5px">${parts.join(' · ')}</div>
      ${meinersCapacity ? `<div style="color:#6A7E8E;font-size:9.5px">${esc(meinersCapacity.detail)}</div>` : ''}
    </div>`;
  });
  return `<div style="font-size:11px;color:#2E3840;line-height:1.5">
    <div style="font-size:13px;font-weight:700;margin-bottom:2px">Gas Transmission · ${systems.length} system${systems.length !== 1 ? 's' : ''}</div>
    ${rows.join('')}
  </div>`;
}

function buildSubstationAnnotation(
  parcel: ParcelDetail,
  source: mapboxgl.GeoJSONSource,
  mapLayers: BriefingMapLayers,
) {
  const center: [number, number] = [Number(parcel.lng), Number(parcel.lat)];

  let closestPoint: [number, number] | null = null;
  let closestDist = Infinity;

  if (mapLayers.substations?.features?.length) {
    for (const f of mapLayers.substations.features) {
      const pt = nearestPointOnGeometry(f.geometry, center);
      if (pt) {
        const d = Math.hypot(pt[0] - center[0], pt[1] - center[1]);
        if (d < closestDist) {
          closestDist = d;
          closestPoint = pt;
        }
      }
    }
  }

  if (!closestPoint) {
    source.setData(EMPTY_FC);
    return;
  }

  const label = parcel.nearest_substation_miles != null
    ? `${Number(parcel.nearest_substation_miles).toFixed(1)} mi`
    : '';

  const midpoint: [number, number] = [
    (center[0] + closestPoint[0]) / 2,
    (center[1] + closestPoint[1]) / 2,
  ];

  const fc: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [center, closestPoint] },
        properties: {},
      },
      {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: midpoint },
        properties: { label },
      },
    ],
  };

  source.setData(fc);
}

// Find the nearest point on a geometry to a target point.
// For LineStrings, projects onto each segment to find the true closest point
// (not just the nearest vertex).
function nearestPointOnGeometry(geometry: GeoJSON.Geometry, target: [number, number]): [number, number] | null {
  const allCoords = extractLineCoords(geometry);
  if (allCoords.length === 0) return null;

  let bestPoint: [number, number] | null = null;
  let bestDist = Infinity;

  for (const line of allCoords) {
    for (let i = 0; i < line.length - 1; i++) {
      const a = line[i] as [number, number];
      const b = line[i + 1] as [number, number];
      const pt = nearestPointOnSegment(target, a, b);
      const d = Math.hypot(pt[0] - target[0], pt[1] - target[1]);
      if (d < bestDist) {
        bestDist = d;
        bestPoint = pt;
      }
    }
    // Also check isolated points (for Point geometries with single coord)
    if (line.length === 1) {
      const pt = line[0] as [number, number];
      const d = Math.hypot(pt[0] - target[0], pt[1] - target[1]);
      if (d < bestDist) {
        bestDist = d;
        bestPoint = pt;
      }
    }
  }

  return bestPoint;
}

// Project point P onto segment AB, clamped to the segment endpoints
function nearestPointOnSegment(p: [number, number], a: [number, number], b: [number, number]): [number, number] {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return a; // degenerate segment

  let t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return [a[0] + t * dx, a[1] + t * dy];
}

// Bounding box across a set of geometries, as fitBounds' [[w,s],[e,n]].
// Walks the same flattened coordinate arrays extractLineCoords already yields,
// which covers Polygon and MultiPolygon rings.
function geometryBounds(geometries: GeoJSON.Geometry[]): [[number, number], [number, number]] | null {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const geometry of geometries) {
    for (const line of extractLineCoords(geometry)) {
      for (const c of line) {
        if (c[0] < minX) minX = c[0];
        if (c[0] > maxX) maxX = c[0];
        if (c[1] < minY) minY = c[1];
        if (c[1] > maxY) maxY = c[1];
      }
    }
  }
  return Number.isFinite(minX) ? [[minX, minY], [maxX, maxY]] : null;
}

// Extract all coordinate arrays from a geometry as arrays of [lng, lat] lines
function extractLineCoords(geometry: GeoJSON.Geometry): number[][][] {
  switch (geometry.type) {
    case 'Point':
      return [[[geometry.coordinates[0], geometry.coordinates[1]]]];
    case 'LineString':
      return [geometry.coordinates as number[][]];
    case 'MultiLineString':
      return geometry.coordinates as number[][][];
    case 'Polygon':
      return geometry.coordinates as number[][][];
    case 'MultiPolygon':
      return (geometry.coordinates as number[][][][]).flat();
    default:
      return [];
  }
}
